import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useParams, useNavigate, Link } from 'react-router-dom';
import { ADMIN_SECTIONS, ADMIN_SECTION_BY_ID } from '../lib/adminSections';
import { ShieldCheck, SlidersHorizontal, Users, Save, GripVertical, RotateCcw, Palette, FileText, Plus, Trash2, Pencil, Copy, X, Mail, Send, Clock, LayoutTemplate, Check, ChevronUp, ChevronDown, Eye, EyeOff, CornerDownRight, CornerLeftUp, FolderTree, ExternalLink, ListTree, Cog, KeyRound, Activity, Images, Cloud, RefreshCw, UserPlus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { NAV_ITEMS, DEFAULT_PERMISSIONS, DEFAULT_SETTINGS, FONT_OPTIONS, PAGES, INACTIVE_CLASSES } from '../mock';
import { resolveMenu, serializeMenu, locateNode, containsId, flattenGroups, groupLandingPath } from '../lib/menu';
import AccessGate from '../components/AccessGate';
import AnalyticsTab from './AnalyticsTab';
import PhotoModerationTab from './PhotoModerationTab';
import AdminActionsTab from './AdminActionsTab';
import EventsAdminTab from './EventsAdminTab';
import GoogleWorkspaceTab from './GoogleWorkspaceTab';
import MemberEditModal from '../components/MemberEditModal';
import CommsDialog from '../components/CommsDialog';
import { GroupOverviewEditor } from '../components/GroupOverviewEditor';
import { NewMeetingButton } from '../components/NewMeetingButton';
import { FlyerBuilder } from '../components/FlyerBuilder';
import MeetingsAdminTab from './MeetingsAdminTab';
import FacesTab from './FacesTab';
import HistoryTab from './HistoryTab';
import { MotionsTab, PaymentsTab, InvoicesTab, EmailsTab } from './RegistryTabs';
import { SmsSettingsCard } from '../components/SmsSettingsCard';
import { CorrespondenceList } from '../components/CorrespondenceList';
import { EmailHealthBadge, EmailHealthCard } from '../components/EmailHealth';
import { healthStatus } from '../lib/email';
import BankingTab from './BankingTab';
import OrganisationTab from './OrganisationTab';
import TagsTab from './TagsTab';
import ShopAdminTab from './ShopAdminTab';
import SubgroupsAdminTab from './SubgroupsAdminTab';
import ArticlesTab from './ArticlesTab';
import GalleryEditorTab from './GalleryEditorTab';
import GovernanceDocsTab from './GovernanceDocsTab';
import MigrationTab from './MigrationTab';
import PitchDeckTab from './PitchDeckTab';
import PhotoBooksAdminTab from './PhotoBooksAdminTab';
import RafflesAdminTab from './RafflesAdminTab';
import TransactionsTab from './TransactionsTab';
import EnquiriesTab from './EnquiriesTab';
import EmailQueueTab from './EmailQueueTab';
import AlbumsAdminTab from './AlbumsAdminTab';
import MessageTab from './MessageTab';
import ApplicationsTab from './ApplicationsTab';
import RenewalsTab from './RenewalsTab';
import InboxTab from './InboxTab';
import HelpTab from './HelpTab';
import OpsDashboard from './OpsDashboard';
import TagMultiSelect from '../components/TagMultiSelect';
import TagFilter from '../components/TagFilter';
import { sanitizeHtml } from '../lib/sanitizeHtml';
import { useSortable, SortTh, TableSearch } from '../components/SortableTable';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import AlphaColorPicker from '../components/AlphaColorPicker';
import ObjectHistory from '../components/ObjectHistory';
import { currentRoles } from '../components/AppointmentsEditor';
import useDragReorder from '../components/useDragReorder';
import BirthdayGreetingsCard from '../components/BirthdayGreetingsCard';
import { PasswordInput } from '../components/PasswordReveal';

const PARENT_OPTIONS = [
  { value: 'top', label: 'Top level (main menu)' },
  { value: 'Members', label: 'Under: Members' },
  { value: 'Publications', label: 'Under: Publications' },
  { value: 'History', label: 'Under: History' },
  { value: 'none', label: 'Hidden from menu (direct link only)' },
];

const COLOR_FIELDS = [
  { key: 'maroon', label: 'Header' },
  { key: 'navy', label: 'Navigation Bar' },
  { key: 'sage', label: 'Page Background' },
  { key: 'olive', label: 'Footer' },
  { key: 'gold', label: 'Accent (Gold)' },
  { key: 'ink', label: 'Body Text' },
  { key: 'cream', label: 'Header Text / Light' },
];

// Flatten pages for the permission matrix (all pages are DB records now).
function flatPages(customPages) {
  const out = (customPages || []).map((p) => ({ key: p.page_key, label: p.nav_label || p.title }));
  out.push({ key: 'admin', label: 'Admin Panel' });
  return out;
}

export default function AdminPanel() {
  const { permissions, updatePermissions, users, refreshUsers, updateUserClass, updateUserFields, settings, updateSettings, canAccess,
    customPages, createPage, updatePage, deletePage, convertPage,
    getTemplates, saveTemplate, applyTemplate, deleteTemplate, setUserTags, bulkUserTags,
    getGroupRecipients, sendGroupEmail, getGroupEmailHistory, importMembers, importMembersCsv,
    getEmailSettings, updateEmailSettings, setUserPassword, sendTestEmail, getMemberActivity,
    memberClasses, classLabel, addMemberClass, renameMemberClass, deleteMemberClass, syncMembersFromSheet, downloadTestPlan, commsMergeFields, requestDetailsUpdate, structureReportPdfUrl, boardFlyerPdfUrl } = useAuth();
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(permissions)));
  const [appDraft, setAppDraft] = useState(() => JSON.parse(JSON.stringify(settings || DEFAULT_SETTINGS)));
  const { section } = useParams();
  const navigate = useNavigate();
  const tab = section || 'dashboard';
  const setTab = (id) => navigate(id === 'dashboard' ? '/admin' : `/admin/${id}`);
  const [saved, setSaved] = useState(false);
  const [newClassLabel, setNewClassLabel] = useState('');
  const [classBusy, setClassBusy] = useState(false);
  const [editingClassKey, setEditingClassKey] = useState(null);
  const [editingClassLabel, setEditingClassLabel] = useState('');
  const [appSaved, setAppSaved] = useState(false);
  const emptyPage = { title: '', nav_label: '', parent: 'top', intro: '', body: '', html: '', draft: false, publish_at: '', allowed_classes: ['admin'], edit_classes: [], edit_users: [] };
  const [pageForm, setPageForm] = useState(emptyPage);
  const [editingKey, setEditingKey] = useState(null);
  const editingPage = (customPages || []).find((p) => p.page_key === editingKey) || null;
  const [editingBuiltin, setEditingBuiltin] = useState(false);
  const [editingComponent, setEditingComponent] = useState(false);
  const [pageBusy, setPageBusy] = useState(false);
  const pages = flatPages(customPages);

  // Deep-link: /admin/pages?edit=<page_key> opens that page's editor directly.
  const location = useLocation();
  const deepLinkApplied = useRef(false);
  useEffect(() => {
    if (deepLinkApplied.current) return;
    const params = new URLSearchParams(location.search);
    const wantEdit = params.get('edit');
    if (!wantEdit || tab !== 'pages') return;
    if (!customPages || !customPages.length) return; // wait for pages to load
    const target = customPages.find((p) => p.page_key === wantEdit);
    if (target) {
      startEdit(target);
      setTimeout(() => {
        const el = document.querySelector('[data-testid="page-edit-form"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    }
    deepLinkApplied.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, customPages, tab]);

  // ---- Email settings (Settings tab) ----
  const [emailCfg, setEmailCfg] = useState(null);
  const [emailCfgBusy, setEmailCfgBusy] = useState(false);
  const [emailCfgSaved, setEmailCfgSaved] = useState(false);
  const [emailCfgErr, setEmailCfgErr] = useState('');
  const [testTo, setTestTo] = useState('');
  const [testBusy, setTestBusy] = useState(false);
  const [testMsg, setTestMsg] = useState(null);
  // ---- Set member password (Members tab) ----
  const [pwUser, setPwUser] = useState(null);
  const [pwValue, setPwValue] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState('');
  // ---- Member activity insights (Members tab) ----
  const [activityUser, setActivityUser] = useState(null);
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  // ---- Edit member modal ----
  const [editUser, setEditUser] = useState(null);
  const [commsOpen, setCommsOpen] = useState(false);
  const [commsTemplate, setCommsTemplate] = useState('');
  const [msgTemplates, setMsgTemplates] = useState([]);
  const [commsPrefill, setCommsPrefill] = useState(null);
  const openComms = (slug = '') => { setCommsTemplate(slug); setCommsOpen(true); };

  const openActivity = async (u) => {
    setActivityUser(u); setActivity(null); setActivityLoading(true);
    try { setActivity(await getMemberActivity(u.user_id)); }
    catch (e) { setActivity({ error: true }); }
    setActivityLoading(false);
  };

  useEffect(() => {
    if (tab === 'members' && msgTemplates.length === 0) {
      commsMergeFields().then((m) => setMsgTemplates(m.templates || [])).catch(() => {});
    }
  }, [tab, msgTemplates.length, commsMergeFields]);

  useEffect(() => {
    if (tab === 'settings' && emailCfg === null) {
      getEmailSettings().then(setEmailCfg).catch(() => setEmailCfg(null));
    }
  }, [tab, emailCfg, getEmailSettings]);

  const saveEmailCfg = async () => {
    setEmailCfgBusy(true); setEmailCfgErr(''); setEmailCfgSaved(false);
    try {
      const payload = {
        provider: emailCfg.provider,
        from_email: emailCfg.from_email,
        from_name: emailCfg.from_name,
        smtp_host: emailCfg.smtp_host,
        smtp_port: Number(emailCfg.smtp_port) || 587,
        smtp_username: emailCfg.smtp_username,
        smtp_use_tls: emailCfg.smtp_use_tls,
      };
      // Only send secrets when the admin typed a new value.
      if (emailCfg.smtp_password) payload.smtp_password = emailCfg.smtp_password;
      if (emailCfg.resend_api_key) payload.resend_api_key = emailCfg.resend_api_key;
      const res = await updateEmailSettings(payload);
      setEmailCfg({ ...res, smtp_password: '', resend_api_key: '' });
      setEmailCfgSaved(true);
    } catch (e) {
      setEmailCfgErr((e.response && e.response.data && e.response.data.detail) || 'Could not save. Please try again.');
    }
    setEmailCfgBusy(false);
  };

  const doSendTestEmail = async () => {
    setTestBusy(true); setTestMsg(null);
    try {
      const res = await sendTestEmail(testTo.trim() || undefined);
      if (res.ok) setTestMsg({ ok: true, text: `Test email sent to ${res.to} via ${res.provider}.` });
      else setTestMsg({ ok: false, text: res.detail || 'Email was not sent.' });
    } catch (e) {
      setTestMsg({ ok: false, text: (e.response && e.response.data && e.response.data.detail) || 'Could not send test email.' });
    }
    setTestBusy(false);
  };

  const submitPassword = async () => {
    if (!pwUser) return;
    setPwBusy(true); setPwMsg('');
    try {
      await setUserPassword(pwUser.user_id, pwValue);
      setPwMsg('Password set.');
      setTimeout(() => { setPwUser(null); setPwValue(''); setPwMsg(''); }, 900);
    } catch (e) {
      setPwMsg((e.response && e.response.data && e.response.data.detail) || 'Could not set password.');
    }
    setPwBusy(false);
  };

  useEffect(() => { refreshUsers(); }, [refreshUsers]);
  useEffect(() => { setDraft(JSON.parse(JSON.stringify(permissions))); }, [permissions]);
  useEffect(() => { if (settings) setAppDraft(JSON.parse(JSON.stringify(settings))); }, [settings]);

  const toggle = (pageKey, cls) => {
    setSaved(false);
    setDraft((prev) => {
      const current = prev[pageKey] || [];
      const next = current.includes(cls) ? current.filter((c) => c !== cls) : [...current, cls];
      return { ...prev, [pageKey]: next };
    });
  };

  const save = async () => { await updatePermissions(draft); setSaved(true); };
  const reset = () => { setDraft(JSON.parse(JSON.stringify(DEFAULT_PERMISSIONS))); setSaved(false); };

  // Appearance helpers
  const setColor = (key, val) => { setAppSaved(false); setAppDraft((p) => ({ ...p, colors: { ...p.colors, [key]: val } })); };
  const setFont = (key, val) => { setAppSaved(false); setAppDraft((p) => ({ ...p, fonts: { ...p.fonts, [key]: val } })); };
  const setHeader = (key, val) => { setAppSaved(false); setAppDraft((p) => ({ ...p, header: { ...p.header, [key]: val } })); };
  const setLayoutFlag = (key, val) => { setAppSaved(false); setAppDraft((p) => ({ ...p, layout: { ...p.layout, [key]: val } })); };
  const toggleHiddenNav = (label) => {
    setAppSaved(false);
    setAppDraft((p) => {
      const hidden = p.layout.hiddenNav || [];
      const next = hidden.includes(label) ? hidden.filter((l) => l !== label) : [...hidden, label];
      return { ...p, layout: { ...p.layout, hiddenNav: next } };
    });
  };
  const saveAppearance = async () => { await updateSettings(appDraft); setAppSaved(true); };
  const resetAppearance = () => { setAppDraft(JSON.parse(JSON.stringify(DEFAULT_SETTINGS))); setAppSaved(false); };

  // ---- Appearance templates ----
  const [templates, setTemplates] = useState([]);
  const [tplName, setTplName] = useState('');
  const [tplBusy, setTplBusy] = useState(false);
  const [tplMsg, setTplMsg] = useState('');
  const loadTemplates = async () => {
    try { setTemplates(await getTemplates()); } catch (e) { setTemplates([]); }
  };
  useEffect(() => { if (tab === 'appearance') loadTemplates(); /* eslint-disable-next-line */ }, [tab]);
  const saveCurrentTemplate = async () => {
    if (!tplName.trim()) return;
    setTplBusy(true); setTplMsg('');
    try { await saveTemplate(tplName.trim()); setTplName(''); setTplMsg('Template saved.'); await loadTemplates(); }
    catch (e) { setTplMsg('Could not save template.'); }
    setTplBusy(false);
  };
  const applyTpl = async (t) => {
    setTplBusy(true); setTplMsg('');
    try { const s = await applyTemplate(t.template_id); setAppDraft(JSON.parse(JSON.stringify(s))); setTplMsg(`Applied “${t.name}”.`); }
    catch (e) { setTplMsg('Could not apply template.'); }
    setTplBusy(false);
  };
  const removeTpl = async (t) => {
    if (!window.confirm(`Delete template “${t.name}”?`)) return;
    setTplBusy(true); setTplMsg('');
    try { await deleteTemplate(t.template_id); await loadTemplates(); }
    catch (e) { setTplMsg('Could not delete template.'); }
    setTplBusy(false);
  };

  // ---- Pages CMS handlers ----
  const startNew = () => { setEditingKey(null); setEditingBuiltin(false); setEditingComponent(false); setPageForm(emptyPage); };
  const isoToLocalInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const relTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)} min ago`;
    if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
    if (s < 604800) return `${Math.floor(s / 86400)} d ago`;
    return d.toLocaleDateString();
  };
  const startEdit = (p) => {
    setEditingKey(p.page_key);
    setEditingBuiltin(!!p.builtin);
    setEditingComponent(!!p.component);
    setPageForm({
      title: p.title, nav_label: p.nav_label || '', parent: p.parent || 'top',
      intro: p.intro || '', body: p.body || '', html: p.html || '', draft: !!p.draft,
      publish_at: isoToLocalInput(p.publish_at), allowed_classes: p.allowed_classes || ['admin'],
      edit_classes: p.edit_classes || [], edit_users: p.edit_users || [],
    });
  };
  const togglePageClass = (cls) => setPageForm((f) => ({
    ...f,
    allowed_classes: f.allowed_classes.includes(cls) ? f.allowed_classes.filter((c) => c !== cls) : [...f.allowed_classes, cls],
  }));
  const togglePageEditClass = (cls) => setPageForm((f) => ({
    ...f,
    edit_classes: (f.edit_classes || []).includes(cls) ? f.edit_classes.filter((c) => c !== cls) : [...(f.edit_classes || []), cls],
  }));
  const submitPage = async () => {
    if (!pageForm.title.trim()) return;
    setPageBusy(true);
    const payload = {
      ...pageForm,
      publish_at: pageForm.draft && pageForm.publish_at ? new Date(pageForm.publish_at).toISOString() : null,
    };
    try {
      if (editingKey) await updatePage(editingKey, payload);
      else await createPage(payload);
      startNew();
    } catch (e) {
      console.error('Page save failed', e);
      window.alert('Could not save the page. Please check your connection and try again.');
    }
    setPageBusy(false);
  };
  const removePage = async (p) => {
    if (!window.confirm(`Delete page \u201c${p.title}\u201d? This cannot be undone.`)) return;
    setPageBusy(true);
    try { await deletePage(p.page_key); if (editingKey === p.page_key) startNew(); }
    catch (e) {
      console.error('Page delete failed', e);
      window.alert('Could not delete the page. Please try again.');
    }
    setPageBusy(false);
  };
  // Build editable intro/body text from a built-in page's rich schema.
  const schemaToText = (pageKey) => {
    const s = PAGES[pageKey] || {};
    const parts = [];
    if (s.paragraphs) parts.push(...s.paragraphs);
    if (s.quote) parts.push(s.quote);
    if (s.list) parts.push(...s.list.map((li) => `\u2022 ${li}`));
    return { intro: s.intro || '', body: parts.join('\n\n') };
  };
  const convertBuiltin = async (pageKey) => {
    if (!window.confirm('Convert this to a normal editable page? Its text is kept, but the rich layout (tables, images, buttons) is replaced by editable text.')) return;
    const src = customByKey(pageKey);
    const t = schemaToText(pageKey);
    setPageBusy(true);
    try {
      await convertPage(pageKey, { intro: src && src.intro ? src.intro : t.intro, body: src && src.body ? src.body : t.body });
      const refreshed = { ...(src || {}), page_key: pageKey, builtin: false, intro: (src && src.intro) || t.intro, body: (src && src.body) || t.body };
      startEdit(refreshed);
    } catch (e) {
      console.error('Convert failed', e);
      window.alert('Could not convert the page. Please try again.');
    }
    setPageBusy(false);
  };

  const duplicatePage = async (pageKey) => {
    const src = customByKey(pageKey);
    if (!src) return;
    let intro = src.intro || '';
    let body = src.body || '';
    if (src.builtin && PAGES[pageKey]) {
      const s = PAGES[pageKey];
      intro = intro || s.intro || '';
      if (!body) {
        const parts = [];
        if (s.paragraphs) parts.push(...s.paragraphs);
        if (s.quote) parts.push(s.quote);
        if (s.list) parts.push(...s.list.map((li) => `\u2022 ${li}`));
        body = parts.join('\n\n');
      }
    }
    setPageBusy(true);
    try {
      const created = await createPage({
        title: `${src.title} (copy)`,
        nav_label: '',
        parent: src.parent && src.parent !== 'none' ? src.parent : 'top',
        intro,
        body,
        allowed_classes: src.allowed_classes || ['admin'],
      });
      if (created) startEdit(created);
    } catch (e) {
      console.error('Duplicate failed', e);
      window.alert('Could not duplicate the page. Please try again.');
    }
    setPageBusy(false);
  };

  // ---- Menu tree (add / delete / move / reparent / hide) ----
  const [menuDraft, setMenuDraft] = useState([]);
  const [groupsDraft, setGroupsDraft] = useState([]);
  const [menuSaved, setMenuSaved] = useState(false);
  const [focusedId, setFocusedId] = useState(null);
  const rowRefs = useRef({});
  const pendingFocus = useRef(false);
  const clone = (x) => JSON.parse(JSON.stringify(x));

  useEffect(() => {
    const cg = (settings && settings.customGroups) || [];
    setGroupsDraft(cg);
    setMenuDraft(resolveMenu({ customPages, menuTree: (settings && settings.menuTree) || [], customGroups: cg }));
  }, [customPages, settings]);

  useEffect(() => {
    if (pendingFocus.current && focusedId && rowRefs.current[focusedId]) {
      rowRefs.current[focusedId].focus();
      pendingFocus.current = false;
    }
  }, [menuDraft, focusedId]);

  const moveById = (id, dir) => {
    setMenuSaved(false);
    setFocusedId(id);
    pendingFocus.current = true;
    setMenuDraft((prev) => {
      const tree = clone(prev);
      const loc = locateNode(tree, id);
      if (!loc) return prev;
      const j = loc.index + dir;
      if (j < 0 || j >= loc.arr.length) return prev;
      [loc.arr[loc.index], loc.arr[j]] = [loc.arr[j], loc.arr[loc.index]];
      return tree;
    });
  };

  // A node can be placed inside any group except itself or one of its own descendants.
  const canNestInto = (tree, id, targetGroupId) => {
    const src = locateNode(tree, id);
    if (!src) return false;
    if (targetGroupId === 'top') return true;
    if (targetGroupId === id) return false;
    if (containsId(src.node, targetGroupId)) return false;
    const tg = locateNode(tree, targetGroupId);
    return !!(tg && tg.node.kind === 'group');
  };

  // Move a node (page or group) to the end of a target group ('top' = top level).
  const reparentNode = (id, target) => {
    setMenuSaved(false);
    setMenuDraft((prev) => {
      const tree = clone(prev);
      if (!canNestInto(tree, id, target)) return prev;
      const src = locateNode(tree, id);
      const [node] = src.arr.splice(src.index, 1);
      if (target === 'top') tree.push(node);
      else {
        const g = locateNode(tree, target).node;
        g.children = g.children || [];
        g.children.push(node);
      }
      return tree;
    });
  };

  // Lift a nested node one level up (placed right after its parent group).
  const outdentNode = (id) => {
    setMenuSaved(false);
    setMenuDraft((prev) => {
      const tree = clone(prev);
      const src = locateNode(tree, id);
      if (!src || !src.parent) return prev;
      const [node] = src.arr.splice(src.index, 1);
      const p = locateNode(tree, src.parent.id);
      p.arr.splice(p.index + 1, 0, node);
      return tree;
    });
  };

  // ---- Drag-and-drop: drop on a row's edge to reorder (any list), on a group's middle to nest ----
  const [dragId, setDragId] = useState(null);
  const [dropHint, setDropHint] = useState(null); // { id, zone: 'before' | 'after' | 'inside' }
  const [previewAsClass, setPreviewAsClass] = useState('financial');
  const openPreview = (path) => {
    if (!path) return;
    const sep = path.includes('?') ? '&' : '?';
    window.open(`${window.location.origin}${path}${sep}viewAs=${previewAsClass}`, '_blank', 'noopener');
  };
  const dropZoneFor = (e, node) => {
    const r = e.currentTarget.getBoundingClientRect();
    const y = (e.clientY - r.top) / Math.max(r.height, 1);
    if (node.kind === 'group') {
      if (y < 0.25) return 'before';
      if (y > 0.75) return 'after';
      return 'inside';
    }
    return y < 0.5 ? 'before' : 'after';
  };
  const dropAllowed = (tree, sourceId, targetId, zone) => {
    if (!sourceId || sourceId === targetId) return false;
    const src = locateNode(tree, sourceId);
    if (!src) return false;
    if (zone === 'inside') return canNestInto(tree, sourceId, targetId);
    // reorder: target must not live inside the dragged subtree
    return !containsId(src.node, targetId);
  };
  const dropDrag = (sourceId, targetId, zone) => {
    setDropHint(null);
    setMenuSaved(false);
    setMenuDraft((prev) => {
      const tree = clone(prev);
      if (!dropAllowed(tree, sourceId, targetId, zone)) return prev;
      const src = locateNode(tree, sourceId);
      const [moved] = src.arr.splice(src.index, 1);
      const t = locateNode(tree, targetId);
      if (zone === 'inside') { t.node.children = t.node.children || []; t.node.children.push(moved); }
      else t.arr.splice(zone === 'after' ? t.index + 1 : t.index, 0, moved);
      return tree;
    });
  };

  const toggleHidden = (id) => {
    setMenuSaved(false);
    setMenuDraft((prev) => {
      const tree = clone(prev);
      const loc = locateNode(tree, id);
      if (loc) loc.node.hidden = !loc.node.hidden;
      return tree;
    });
  };

  // Group landing page: '' = dropdown only, 'auto' = generated overview, 'page:<key>' = existing page.
  const setLanding = (id, value) => {
    setMenuSaved(false);
    setMenuDraft((prev) => {
      const tree = clone(prev);
      const loc = locateNode(tree, id);
      if (!loc) return prev;
      loc.node.landing = value || null;
      const target = value && value !== 'auto' ? (customPages || []).find((p) => `page:${p.page_key}` === value) : null;
      loc.node.landingPath = groupLandingPath(loc.node, target ? { [value]: { kind: 'page', path: target.path || `/p/${target.page_key}` } } : null);
      return tree;
    });
  };
  const [overviewEdit, setOverviewEdit] = useState(null); // group node being edited
  const setGroupMeta = (id, patch) => {
    setMenuSaved(false);
    setMenuDraft((prev) => {
      const tree = clone(prev);
      const loc = locateNode(tree, id);
      if (loc) Object.assign(loc.node, patch);
      return tree;
    });
  };
  const descendantPageIds = (node) => {
    const out = [];
    const walk = (nodes) => (nodes || []).forEach((c) => { if (c.kind === 'page') out.push(c.id); else walk(c.children); });
    walk(node.children);
    return out;
  };

  const saveMenu = async () => { await updateSettings({ ...settings, menuTree: serializeMenu(menuDraft), customGroups: groupsDraft }); setMenuSaved(true); };
  const resetMenu = () => { setGroupsDraft([]); setMenuDraft(resolveMenu({ customPages, menuTree: [], customGroups: [] })); setMenuSaved(false); };

  const addGroup = () => {
    const label = window.prompt('New menu group name:');
    if (!label || !label.trim()) return;
    const id = `group:custom:${Math.random().toString(36).slice(2, 10)}`;
    setGroupsDraft((g) => [...g, { id, label: label.trim() }]);
    setMenuDraft((prev) => [...prev, { id, kind: 'group', label: label.trim(), custom: true, hidden: false, children: [] }]);
    setMenuSaved(false);
  };
  const renameGroup = (node) => {
    const label = window.prompt('Rename group:', node.label);
    if (!label || !label.trim()) return;
    setGroupsDraft((g) => g.map((x) => (x.id === node.id ? { ...x, label: label.trim() } : x)));
    setMenuDraft((prev) => {
      const tree = clone(prev);
      const loc = locateNode(tree, node.id);
      if (loc) loc.node.label = label.trim();
      return tree;
    });
    setMenuSaved(false);
  };
  const deleteGroup = (node) => {
    if (!window.confirm(`Delete group \u201c${node.label}\u201d? Its contents move up one level.`)) return;
    setMenuDraft((prev) => {
      const tree = clone(prev);
      const loc = locateNode(tree, node.id);
      if (!loc) return prev;
      loc.arr.splice(loc.index, 1, ...(loc.node.children || []));
      return tree;
    });
    setGroupsDraft((g) => g.filter((x) => x.id !== node.id));
    setMenuSaved(false);
  };

  const addTopLevel = () => { setEditingKey(null); setEditingBuiltin(false); setEditingComponent(false); setPageForm({ ...emptyPage, parent: 'top' }); };
  const addUnder = (label) => { setEditingKey(null); setEditingBuiltin(false); setEditingComponent(false); setPageForm({ ...emptyPage, parent: label }); };
  const customByKey = (pageKey) => (customPages || []).find((p) => p.page_key === pageKey);
  const groupOptions = flattenGroups(menuDraft);
  const parentOptions = [
    ...PARENT_OPTIONS.filter((o) => o.value !== 'none'),
    ...groupsDraft.map((g) => ({ value: g.label, label: `Under: ${g.label}` })),
    { value: 'none', label: 'Hidden from menu (direct link only)' },
  ];

  const onRowKey = (e, id) => {
    if (e.key === 'ArrowUp') { e.preventDefault(); moveById(id, -1); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); moveById(id, 1); }
  };

  // ---- CSV import / export ----
  const csvFileRef = useRef(null);
  const [importMsg, setImportMsg] = useState('');
  const csvEscape = (v) => {
    const s = (v === null || v === undefined) ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const exportCsv = () => {
    const header = ['name', 'email', 'member_class', 'rank', 'unit', 'years_served', 'tags'];
    const lines = [header.join(',')];
    (tagFilter.length || tagExclude.length ? filteredUsers : users).forEach((u) => {
      lines.push([u.name, u.email, u.memberClass, u.rank, u.unit, u.years_served, (u.tags || []).join('; ')].map(csvEscape).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'members.csv'; a.click();
    URL.revokeObjectURL(url);
  };
  const parseCsv = (text) => {
    const rows = []; let field = ''; let row = []; let inQ = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (inQ) {
        if (c === '"') { if (text[i + 1] === '"') { field += '"'; i++; } else inQ = false; }
        else field += c;
      } else if (c === '"') inQ = true;
      else if (c === ',') { row.push(field); field = ''; }
      else if (c === '\n' || c === '\r') {
        if (c === '\r' && text[i + 1] === '\n') i++;
        row.push(field); field = '';
        if (row.length > 1 || row[0] !== '') rows.push(row);
        row = [];
      } else field += c;
    }
    if (field !== '' || row.length) { row.push(field); rows.push(row); }
    return rows;
  };
  const onImportFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setImportMsg('Importing…');
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (rows.length < 2) { setImportMsg('CSV appears empty.'); return; }
      const header = rows[0].map((h) => h.trim().toLowerCase());
      const idx = (name) => header.indexOf(name);
      const payload = rows.slice(1).filter((r) => r.some((c) => c.trim())).map((r) => ({
        email: (r[idx('email')] || '').trim(),
        name: idx('name') >= 0 ? (r[idx('name')] || '').trim() : undefined,
        member_class: idx('member_class') >= 0 ? (r[idx('member_class')] || '').trim() : undefined,
        rank: idx('rank') >= 0 ? (r[idx('rank')] || '').trim() : undefined,
        unit: idx('unit') >= 0 ? (r[idx('unit')] || '').trim() : undefined,
        years_served: idx('years_served') >= 0 ? (r[idx('years_served')] || '').trim() : undefined,
        tags: idx('tags') >= 0 ? (r[idx('tags')] || '').split(/[;|]/).map((t) => t.trim()).filter(Boolean) : undefined,
      }));
      const res = await importMembers(payload);
      setImportMsg(`Imported: ${res.created} added, ${res.updated} updated, ${res.skipped} skipped.`);
    } catch (err) {
      console.error('CSV import failed', err);
      setImportMsg('Import failed. Check the file format and try again.');
    }
    if (csvFileRef.current) csvFileRef.current.value = '';
  };
  const sheetFileRef = React.useRef(null);
  const onImportSheet = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setImportMsg('Importing spreadsheet…');
    try {
      const text = await file.text();
      const res = await importMembersCsv(text);
      setImportMsg(`Spreadsheet import: ${res.created} added, ${res.updated} updated, ${res.skipped} skipped (years: ${(res.year_columns || []).join(', ') || 'none'}).`);
    } catch (err) {
      console.error('Sheet import failed', err);
      setImportMsg('Spreadsheet import failed. Export the sheet as CSV and try again.');
    }
    if (sheetFileRef.current) sheetFileRef.current.value = '';
  };

  const [sheetSyncBusy, setSheetSyncBusy] = useState(false);
  const syncFromGoogleSheet = async () => {
    setSheetSyncBusy(true);
    setImportMsg('Syncing with Google Sheet…');
    try {
      const res = await syncMembersFromSheet();
      setImportMsg(`Google Sheet sync: ${res.created} added, ${res.updated} updated, ${res.skipped} skipped.`);
      await refreshUsers();
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      setImportMsg(detail.includes('not configured') || detail.includes('No Google Sheet')
        ? 'Google Sheet is not set up yet — configure the service account and Sheet ID in Admin → Google Workspace first.'
        : `Google Sheet sync failed. ${detail}`.trim());
    }
    setSheetSyncBusy(false);
  };

  // ---- Renewals ----
  const changeUserClass = async (id, memberClass) => {
    try {
      await updateUserClass(id, memberClass);
      toast.success('Member role updated.');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not update this member.';
      toast.error(msg);
    }
  };

  // ---- Contact tags (grouping & selecting) ----
  const [selectedIds, setSelectedIds] = useState([]);
  const [tagFilter, setTagFilter] = useState([]);
  const [tagExclude, setTagExclude] = useState([]);
  const [tagMatch, setTagMatch] = useState('any');
  const [healthFilter, setHealthFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('');
  const allRoles = Array.from(new Set(users.flatMap(currentRoles))).sort();
  const [bulkTags, setBulkTags] = useState([]);
  const [rowTag, setRowTag] = useState({});
  const allTags = Array.from(new Set(users.flatMap((u) => u.tags || []))).sort();
  const HEALTH_FILTERS = { all: () => true, problems: (u) => ['bad', 'warn'].includes(healthStatus(u)), unchecked: (u) => healthStatus(u) === 'unchecked', none: (u) => healthStatus(u) === 'none' };
  const tagHit = (u) => (!tagFilter.length || (tagMatch === 'all' ? tagFilter.every((t) => (u.tags || []).includes(t)) : tagFilter.some((t) => (u.tags || []).includes(t)))) && !tagExclude.some((t) => (u.tags || []).includes(t));
  const filteredUsers = users.filter(tagHit).filter(HEALTH_FILTERS[healthFilter] || HEALTH_FILTERS.all).filter((u) => !roleFilter || (roleFilter === '__any' ? currentRoles(u).length > 0 : currentRoles(u).includes(roleFilter)));
  const { sorted: sortedUsers, sort: memberSort, toggle: toggleMemberSort, query: memberQuery, setQuery: setMemberQuery } = useSortable(filteredUsers, { searchFields: [(u) => u.name, (u) => u.rank, (u) => u.unit, (u) => u.email, (u) => u.phone, (u) => u.mobile, (u) => u.address, (u) => u.suburb, (u) => u.postcode, (u) => u.status, (u) => u.note, (u) => classLabel(u.memberClass), (u) => (u.tags || []).join(' ')] });
  const toggleSelect = (id) => setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = sortedUsers.length > 0 && sortedUsers.every((u) => selectedIds.includes(u.user_id));
  const toggleSelectAll = () => {
    const shown = sortedUsers.map((u) => u.user_id);
    setSelectedIds((s) => (allSelected ? s.filter((id) => !shown.includes(id)) : Array.from(new Set([...s, ...shown]))));
  };
  const addRowTag = async (id) => {
    const t = (rowTag[id] || '').trim();
    if (!t) return;
    const u = users.find((x) => x.user_id === id);
    try {
      await setUserTags(id, Array.from(new Set([...(u.tags || []), t])));
      setRowTag((r) => ({ ...r, [id]: '' }));
    } catch (e) {
      console.error('Add tag failed', e);
      window.alert('Could not add the tag. Please try again.');
    }
  };
  const removeRowTag = async (id, tag) => {
    const u = users.find((x) => x.user_id === id);
    try { await setUserTags(id, (u.tags || []).filter((t) => t !== tag)); }
    catch (e) { console.error('Remove tag failed', e); window.alert('Could not remove the tag. Please try again.'); }
  };
  const applyBulkTag = async (mode) => {
    const tags = bulkTags.map((t) => t.trim()).filter(Boolean);
    if (!tags.length || selectedIds.length === 0) return;
    try {
      await bulkUserTags({ user_ids: selectedIds, add: mode === 'add' ? tags : [], remove: mode === 'remove' ? tags : [] });
      setBulkTags([]);
    } catch (e) {
      console.error('Bulk tag failed', e);
      window.alert('Could not update tags for the selected members. Please try again.');
    }
  };

  // ---- Email a group (by tags) ----
  const [emailTags, setEmailTags] = useState([]);
  const [emailMatch, setEmailMatch] = useState('any');
  const [emailIncludeInactive, setEmailIncludeInactive] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [emailConfigured, setEmailConfigured] = useState(true);
  const [emailHistory, setEmailHistory] = useState([]);
  const [expandedSend, setExpandedSend] = useState(null);
  const loadEmailHistory = () => { getGroupEmailHistory().then(setEmailHistory).catch(() => setEmailHistory([])); };
  useEffect(() => {
    if (tab === 'email') {
      getGroupRecipients([], 'any').then((d) => setEmailConfigured(d.email_configured)).catch(() => {});
      loadEmailHistory();
    }
    /* eslint-disable-next-line */
  }, [tab]);
  const toggleEmailTag = (t) => setEmailTags((s) => (s.includes(t) ? s.filter((x) => x !== t) : [...s, t]));
  const emailRecipients = users.filter((u) => {
    if (emailTags.length === 0 || !u.email) return false;
    if (!emailIncludeInactive && INACTIVE_CLASSES.includes(u.member_class)) return false;
    const ut = new Set(u.tags || []);
    return emailMatch === 'all' ? emailTags.every((t) => ut.has(t)) : emailTags.some((t) => ut.has(t));
  });
  const sendEmailGroup = async () => {
    if (!emailTags.length || !emailSubject.trim() || !emailBody.trim()) return;
    setEmailBusy(true); setEmailResult(null);
    try {
      const res = await sendGroupEmail({ tags: emailTags, match: emailMatch, subject: emailSubject, body: emailBody, include_inactive: emailIncludeInactive });
      setEmailResult(res);
      setEmailConfigured(res.email_configured);
      loadEmailHistory();
    } catch (e) {
      console.error('Group email failed', e);
      setEmailResult({ error: (e && e.response && e.response.data && e.response.data.detail) || 'Could not send. Please try again.' });
    }
    setEmailBusy(false);
  };

  const renderMenuRow = (node, depth, hasParent) => {
    const isPage = node.kind === 'page';
    const badge = node.kind === 'group' ? (node.custom ? 'Group' : 'Menu') : 'Page';
    const hint = dropHint && dropHint.id === node.id ? dropHint.zone : null;
    const edgeStyle = hint === 'before' ? { boxShadow: 'inset 0 3px 0 var(--navy)' }
      : hint === 'after' ? { boxShadow: 'inset 0 -3px 0 var(--navy)' }
      : hint === 'inside' ? { boxShadow: 'inset 0 0 0 2px var(--navy)', background: 'rgba(31,56,100,0.08)' } : {};
    return (
      <div
        key={node.id}
        tabIndex={0}
        draggable
        onDragStart={(e) => { e.stopPropagation(); setDragId(node.id); e.dataTransfer.effectAllowed = 'move'; }}
        onDragOver={(e) => {
          e.preventDefault(); e.stopPropagation();
          const zone = dropZoneFor(e, node);
          const ok = dropAllowed(menuDraft, dragId, node.id, zone);
          e.dataTransfer.dropEffect = ok ? 'move' : 'none';
          setDropHint(ok ? { id: node.id, zone } : null);
        }}
        onDragLeave={() => setDropHint((h) => (h && h.id === node.id ? null : h))}
        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); dropDrag(dragId, node.id, dropZoneFor(e, node)); setDragId(null); }}
        onDragEnd={() => { setDragId(null); setDropHint(null); }}
        ref={(el) => { rowRefs.current[node.id] = el; }}
        onFocus={() => setFocusedId(node.id)}
        onKeyDown={(e) => onRowKey(e, node.id)}
        data-testid={`menu-node-${node.id}`}
        data-depth={depth}
        data-drop-zone={hint || undefined}
        title={node.kind === 'group' ? 'Drag to reorder; drop items onto the middle of this group to nest them inside' : 'Drag to reorder or drop onto a group to nest'}
        className="flex items-center gap-2 rounded border px-2 py-2 bg-white outline-none focus:ring-2 cursor-move"
        style={{ borderColor: node.hidden ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.14)', opacity: node.hidden ? 0.55 : (dragId === node.id ? 0.4 : 1), marginLeft: depth * 22, ...edgeStyle }}
      >
        {hasParent && <CornerDownRight size={13} style={{ color: 'var(--olive)' }} />}
        {node.kind === 'group' && <FolderTree size={13} style={{ color: 'var(--olive)' }} />}
        <span className="text-sm flex-1 truncate min-w-[72px]" title={node.label}>{node.label}</span>
        <span className="px-1.5 py-0.5 rounded text-[10px] font-title" style={{ background: node.kind === 'group' ? 'var(--sage)' : node.custom ? 'var(--gold)' : '#e6e6e6', color: '#3a2c06' }}>{badge}</span>
        {node.hidden && <span className="px-1.5 py-0.5 rounded text-[10px]" style={{ background: '#eee', color: '#666' }}>Hidden</span>}
        {node.draft && (
          <span className="px-1.5 py-0.5 rounded text-[10px]" data-testid={`menu-draft-badge-${node.id}`} style={{ background: '#fff4e5', color: '#8a5a00' }}>
            {node.publishAt && new Date(node.publishAt).getTime() > Date.now() ? 'Scheduled' : 'Draft'}
          </span>
        )}

        <div className="flex items-center gap-1 flex-shrink-0">
          <button data-testid={`menu-up-${node.id}`} onClick={() => moveById(node.id, -1)} title="Move up" className="p-1 rounded hover:bg-black/5"><ChevronUp size={15} /></button>
          <button data-testid={`menu-down-${node.id}`} onClick={() => moveById(node.id, 1)} title="Move down" className="p-1 rounded hover:bg-black/5"><ChevronDown size={15} /></button>
          {hasParent && (
            <button data-testid={`menu-outdent-${node.id}`} onClick={() => outdentNode(node.id)} title="Move out to parent level" className="p-1 rounded hover:bg-black/5"><CornerLeftUp size={14} /></button>
          )}
          <button data-testid={`menu-hide-${node.id}`} onClick={() => toggleHidden(node.id)} title={node.hidden ? 'Show' : 'Hide'} className="p-1 rounded hover:bg-black/5">
            {node.hidden ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
          <select
            data-testid={`menu-move-${node.id}`}
            value=""
            onChange={(e) => { if (e.target.value) reparentNode(node.id, e.target.value); }}
            title="Move to menu group"
            className="text-xs rounded border px-1 py-1 max-w-[96px]"
            style={{ borderColor: 'rgba(0,0,0,0.2)' }}
          >
            <option value="">Move to…</option>
            <option value="top">Top level</option>
            {groupOptions
              .filter(({ node: g }) => g.id !== node.id && !containsId(node, g.id))
              .map(({ node: g, depth: d }) => (
                <option key={g.id} value={g.id}>{`${'\u00a0\u00a0'.repeat(d)}Under ${g.label}`}</option>
              ))}
          </select>
          {node.kind === 'group' && (
            <>
              <select
                data-testid={`menu-landing-${node.id}`}
                value={node.landing || ''}
                onChange={(e) => setLanding(node.id, e.target.value)}
                title="What happens when this group is clicked"
                className="text-xs rounded border px-1 py-1 max-w-[120px]"
                style={{ borderColor: node.landing ? 'var(--navy)' : 'rgba(0,0,0,0.2)' }}
              >
                <option value="">Click: dropdown</option>
                <option value="auto">Click: overview page</option>
                {(() => {
                  const own = new Set(descendantPageIds(node));
                  const all = customPages || [];
                  const opt = (p) => <option key={p.page_key} value={`page:${p.page_key}`}>{p.nav_label || p.title}</option>;
                  return (
                    <>
                      <optgroup label="Open a page in this group">{all.filter((p) => own.has(`page:${p.page_key}`)).map(opt)}</optgroup>
                      <optgroup label="Open another page">{all.filter((p) => !own.has(`page:${p.page_key}`)).map(opt)}</optgroup>
                    </>
                  );
                })()}
              </select>
              {node.landing === 'auto' && (
                <button data-testid={`menu-overview-edit-${node.id}`} onClick={() => setOverviewEdit(node)} title="Edit overview page intro & header image" className="p-1 rounded" style={{ background: 'var(--sage)', color: '#3a2c06' }}><FileText size={13} /></button>
              )}
              {node.landingPath && (
                <a href={node.landingPath} target="_blank" rel="noopener noreferrer" data-testid={`menu-landing-link-${node.id}`} title={`Open landing page ${node.landingPath}`} className="p-1 rounded" style={{ background: 'var(--gold)', color: '#3a2c06' }}><ExternalLink size={13} /></a>
              )}
              <button data-testid={`menu-add-under-${node.id}`} onClick={() => addUnder(node.label)} title={`Add page under ${node.label}`} className="p-1 rounded" style={{ background: 'var(--navy)', color: 'var(--cream)' }}><Plus size={13} /></button>
              {node.custom && (
                <>
                  <button data-testid={`menu-rename-${node.id}`} onClick={() => renameGroup(node)} title="Rename group" className="p-1 rounded" style={{ background: 'var(--olive)', color: 'var(--cream)' }}><Pencil size={13} /></button>
                  <button data-testid={`menu-delete-group-${node.id}`} onClick={() => deleteGroup(node)} title="Delete group" className="p-1 rounded" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}><Trash2 size={13} /></button>
                </>
              )}
            </>
          )}
          {isPage && (
            <>
              <button onClick={() => openPreview(node.path)} title={`Preview this page as ${classLabel(previewAsClass)}`} data-testid={`menu-preview-${node.id}`} className="p-1 rounded" style={{ background: 'var(--gold)', color: '#3a2c06' }}><Eye size={13} /></button>
              <button onClick={() => duplicatePage(node.pageKey)} title="Duplicate" data-testid={`menu-duplicate-${node.id}`} className="p-1 rounded" style={{ background: 'var(--olive)', color: 'var(--cream)' }}><Copy size={13} /></button>
              <button onClick={() => startEdit(customByKey(node.pageKey))} title="Edit" className="p-1 rounded" style={{ background: 'var(--navy)', color: 'var(--cream)' }}><Pencil size={13} /></button>
              <button onClick={() => { const p = customByKey(node.pageKey); if (p) removePage(p); }} title="Delete" className="p-1 rounded" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}><Trash2 size={13} /></button>
            </>
          )}
        </div>
      </div>
    );
  };

  const sectionOrder = (settings && settings.adminSectionOrder) || [];
  const orderedSections = [...ADMIN_SECTIONS].sort((a, b) => {
    const ia = sectionOrder.indexOf(a.id), ib = sectionOrder.indexOf(b.id);
    return (ia < 0 ? 1e6 + ADMIN_SECTIONS.indexOf(a) : ia) - (ib < 0 ? 1e6 + ADMIN_SECTIONS.indexOf(b) : ib);
  });
  const accessibleSections = orderedSections.filter((s) => canAccess(s.perm));
  const saveSectionOrder = async (next) => {
    const visible = next.map((s) => s.id);
    const full = [...visible, ...orderedSections.map((s) => s.id).filter((id) => !visible.includes(id))];
    try { await updateSettings({ ...settings, adminSectionOrder: full }); toast.success('Admin layout saved.'); }
    catch { toast.error('Could not save the admin layout.'); }
  };
  const sectionDnd = useDragReorder({ onReorder: saveSectionOrder });
  const [bankUnmatched, setBankUnmatched] = useState(0);
  const [readiness, setReadiness] = useState(null);
  const [heldEmails, setHeldEmails] = useState(0);
  useEffect(() => {
    if (tab !== 'dashboard') return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/basiq/summary`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null)).then((d) => { if (d) setBankUnmatched(d.unmatched_deposits || 0); }).catch(() => {});
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/security/production-readiness`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null)).then((d) => { if (d) setReadiness(d); }).catch(() => {});
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/email-queue/count`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null)).then((d) => { if (d) setHeldEmails(d.held || 0); }).catch(() => {});
  }, [tab]);
  const currentSection = tab === 'dashboard' ? null : ADMIN_SECTION_BY_ID[tab];
  const gateKey = tab === 'dashboard'
    ? (accessibleSections.length ? accessibleSections[0].perm : 'admin')
    : (currentSection ? currentSection.perm : 'admin');

  return (
    <AccessGate pageKey={gateKey}>
      <div className="max-w-6xl mx-auto px-4 py-10 fade-in">
        {tab === 'dashboard' ? (
          <>
            <div className="flex items-center gap-3 mb-2">
              <ShieldCheck size={28} style={{ color: 'var(--maroon)' }} />
              <h1 className="font-title text-3xl md:text-4xl" style={{ color: 'var(--maroon)' }}>Admin Dashboard</h1>
            </div>
            <div className="h-1 w-20 mb-6" style={{ background: 'var(--gold)' }} />
            <p className="text-sm mb-4" style={{ color: 'var(--olive)' }}>Choose a section to manage. You only see the sections your role can access.</p>
            <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="admin-quick-actions">
              <NewMeetingButton testId="dashboard-new-meeting" size="sm" />
              <a href={structureReportPdfUrl()} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded font-title px-3 py-1.5 text-xs" style={{ background: 'var(--navy)', color: 'var(--cream)' }} data-testid="dashboard-structure-report"><FileText size={14} /> Download structure report (PDF)</a>
              <a href={boardFlyerPdfUrl()} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded font-title px-3 py-1.5 text-xs" style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid="dashboard-board-flyer"><FileText size={14} /> Board presentation flyer (PDF)</a>
              <FlyerBuilder />
            </div>
            {readiness && (
              <Link to="/admin/inbox" data-testid="dashboard-readiness-badge" className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full text-sm font-title transition-all hover:shadow-sm" style={{ background: readiness.ready ? '#eef6ee' : '#fdecec', color: readiness.ready ? '#1c6b2c' : '#8a1f11', border: `1px solid ${readiness.ready ? '#1c6b2c' : '#8a1f11'}` }}>
                <ShieldCheck size={15} /> Production readiness: {readiness.ready ? 'GO' : `NO-GO — ${(readiness.checks || []).filter((c) => c.status === 'fail').length} to fix`}
              </Link>
            )}
            <div className="mb-6 rounded-lg border p-4" style={{ borderColor: 'rgba(0,0,0,0.1)', background: 'rgba(122,86,44,0.04)' }} data-testid="admin-test-plan-card">
              <div className="flex items-center gap-2 font-title text-sm mb-1" style={{ color: 'var(--navy)' }}><FileText size={16} /> Test plan</div>
              <p className="text-xs mb-3" style={{ color: 'var(--olive)' }}>Download the QA test plan to hand to the committee or volunteers.</p>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => downloadTestPlan('smoke-csv')} data-testid="download-smoke-csv" className="text-xs px-3 py-1.5 rounded font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>Post-deploy smoke test (CSV)</button>
                <button onClick={() => downloadTestPlan('plan-csv')} data-testid="download-plan-csv" className="text-xs px-3 py-1.5 rounded font-title" style={{ background: 'var(--navy)', color: 'var(--cream)' }}>Full test plan (CSV)</button>
                <button onClick={() => downloadTestPlan('plan-md')} data-testid="download-plan-md" className="text-xs px-3 py-1.5 rounded font-title" style={{ border: '1px solid rgba(0,0,0,0.2)', color: 'var(--navy)' }}>Full test plan (Markdown)</button>
              </div>
            </div>
            <p className="text-xs mb-2 inline-flex items-center gap-1" style={{ color: 'var(--olive)' }} data-testid="admin-layout-hint"><GripVertical size={12} /> Drag the cards (or the section buttons) to arrange them in the order you prefer — saved for everyone.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-dashboard" {...sectionDnd.containerProps(accessibleSections)}>
              {accessibleSections.map((s, i) => (
                <Link key={s.id} to={`/admin/${s.id}`} data-testid={`admin-card-${s.id}`}
                  className="group rounded-lg border bg-white p-5 shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5"
                  style={{ borderColor: sectionDnd.isOver(s.id) ? 'var(--gold)' : 'rgba(0,0,0,0.08)', borderWidth: sectionDnd.isOver(s.id) ? 2 : 1, opacity: sectionDnd.isDragging(s.id) ? 0.4 : 1, cursor: 'grab' }}
                  {...sectionDnd.itemProps(accessibleSections, s, i)} onClick={(e) => { if (sectionDnd.dragging) e.preventDefault(); }}>
                  <div className="flex items-center gap-3 mb-1">
                    <span className="flex items-center justify-center w-10 h-10 rounded-full" style={{ background: 'var(--navy)', color: 'var(--cream)' }}><s.icon size={18} /></span>
                    <h3 className="font-title text-lg" style={{ color: 'var(--maroon)' }}>{s.label}</h3>
                    {s.id === 'banking' && bankUnmatched > 0 && (
                      <span data-testid="banking-card-badge" className="ml-auto text-xs font-title px-2 py-0.5 rounded-full" style={{ background: '#e0b34d', color: '#5a3d05' }}>{bankUnmatched} to match</span>
                    )}
                    {s.id === 'email-queue' && heldEmails > 0 && (
                      <span data-testid="email-queue-card-badge" className="ml-auto text-xs font-title px-2 py-0.5 rounded-full" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>{heldEmails} waiting</span>
                    )}
                  </div>
                  <p className="text-sm" style={{ color: 'var(--olive)' }}>{s.desc}</p>
                </Link>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <Link to="/admin" data-testid="admin-back" className="inline-flex items-center gap-1 text-sm hover:underline" style={{ color: 'var(--navy)' }}>
                <ShieldCheck size={16} /> Admin
              </Link>
              <span style={{ color: 'var(--olive)' }}>/</span>
              <h1 className="font-title text-2xl md:text-3xl" style={{ color: 'var(--maroon)' }}>{currentSection ? currentSection.label : 'Unknown section'}</h1>
            </div>
            <div className="h-1 w-20 mb-6" style={{ background: 'var(--gold)' }} />
            <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-tabs" {...sectionDnd.containerProps(accessibleSections)}>
              {accessibleSections.map((s, i) => (
                <button
                  key={s.id}
                  data-testid={`admin-tab-${s.id}`}
                  onClick={() => setTab(s.id)}
                  title="Drag to rearrange"
                  className="flex items-center gap-2 px-3 py-1.5 rounded font-title text-sm transition-colors whitespace-nowrap"
                  style={{ ...(tab === s.id ? { background: 'var(--navy)', color: 'var(--cream)' } : { background: 'white', color: 'var(--ink)', border: '1px solid rgba(0,0,0,0.12)' }), outline: sectionDnd.isOver(s.id) ? '2px solid var(--gold)' : 'none', opacity: sectionDnd.isDragging(s.id) ? 0.4 : 1, cursor: 'grab' }}
                  {...sectionDnd.itemProps(accessibleSections, s, i)}
                >
                  <s.icon size={15} /> {s.label}
                </button>
              ))}
            </div>
          </>
        )}

        {pwUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} data-testid="set-password-modal">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-title text-lg" style={{ color: 'var(--maroon)' }}>Set password</h3>
                <button onClick={() => { setPwUser(null); setPwValue(''); }} data-testid="set-password-close"><X size={18} /></button>
              </div>
              <p className="text-sm mb-3" style={{ color: 'var(--olive)' }}>
                Set a password for <strong>{pwUser.name}</strong> ({pwUser.email}). They can sign in with this email and password.
              </p>
              <PasswordInput value={pwValue} placeholder="New password (min 8 characters)"
                onChange={(e) => setPwValue(e.target.value)} data-testid="set-password-input" />
              {pwMsg && <p className="text-sm mt-2" style={{ color: pwMsg === 'Password set.' ? '#1c6b2c' : '#8a1f11' }} data-testid="set-password-msg">{pwMsg}</p>}
              <div className="flex justify-end gap-2 mt-4">
                <button onClick={() => { setPwUser(null); setPwValue(''); }} className="px-3 py-2 rounded text-sm" style={{ border: '1px solid rgba(0,0,0,0.15)' }}>Cancel</button>
                <button onClick={submitPassword} disabled={pwBusy || pwValue.length < 8}
                  className="px-4 py-2 rounded text-sm font-semibold" style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: (pwBusy || pwValue.length < 8) ? 0.6 : 1 }}
                  data-testid="set-password-submit">Set password</button>
              </div>
            </div>
          </div>
        )}

        {commsOpen && <CommsDialog initialIds={selectedIds} initialTemplate={commsTemplate} initialSubject={commsPrefill?.subject || ''} initialBody={commsPrefill?.body || ''} onClose={() => { setCommsOpen(false); setCommsPrefill(null); }} />}

        {activityUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} data-testid="member-activity-modal">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[85vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-title text-lg flex items-center gap-2" style={{ color: 'var(--maroon)' }}>
                  <Activity size={18} /> {activityUser.name}
                </h3>
                <button onClick={() => { setActivityUser(null); setActivity(null); }} data-testid="member-activity-close"><X size={18} /></button>
              </div>
              {activityLoading ? (
                <p className="text-sm" style={{ color: 'var(--olive)' }}>Loading…</p>
              ) : !activity || activity.error ? (
                <p className="text-sm" style={{ color: '#8a1f11' }}>Could not load activity.</p>
              ) : (
                <div className="space-y-4">
                  <CorrespondenceList items={activity.correspondence} />
                  <div className="flex gap-6">
                    <div>
                      <div className="text-2xl font-title" style={{ color: 'var(--maroon)' }} data-testid="activity-total">{activity.total_views}</div>
                      <div className="text-xs" style={{ color: 'var(--olive)' }}>Total page views</div>
                    </div>
                    <div>
                      <div className="text-sm font-title" style={{ color: 'var(--navy)' }} data-testid="activity-last-seen">
                        {activity.last_seen ? new Date(activity.last_seen).toLocaleString() : 'Never'}
                      </div>
                      <div className="text-xs" style={{ color: 'var(--olive)' }}>Last seen</div>
                    </div>
                  </div>
                  {activity.total_views === 0 ? (
                    <p className="text-sm" style={{ color: 'var(--olive)' }}>No tracked activity yet for this member.</p>
                  ) : (
                    <>
                      <div>
                        <h4 className="font-title text-sm mb-2" style={{ color: 'var(--navy)' }}>Most-viewed pages</h4>
                        <div className="space-y-1">
                          {activity.top_pages.map((p, i) => (
                            <div key={i} className="flex justify-between text-xs" style={{ color: 'var(--ink)' }}>
                              <span className="truncate pr-2">{p.label}</span><span style={{ color: 'var(--olive)' }}>{p.count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-title text-sm mb-2" style={{ color: 'var(--navy)' }}>Recent visits</h4>
                        <div className="space-y-1 text-xs">
                          {activity.recent.map((r, i) => (
                            <div key={i} className="flex justify-between gap-2" style={{ color: 'var(--ink)' }}>
                              <span className="truncate">{r.path}</span>
                              <span className="whitespace-nowrap" style={{ color: 'var(--olive)' }}>{new Date(r.created_at).toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {editUser && (
          <MemberEditModal
            user={editUser}
            memberClasses={memberClasses}
            onClose={() => setEditUser(null)}
            onSaved={refreshUsers}
          />
        )}

        {tab === 'permissions' && (
          <div>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
              <p className="text-sm" style={{ color: 'var(--olive)' }}>
                Tick which member class may access each page. Each class has its own independent set.
              </p>
              <div className="flex gap-2">
                <button onClick={reset} data-testid="permissions-reset-btn" className="flex items-center gap-1.5 px-3 py-2 rounded text-sm font-title" style={{ background: 'white', border: '1px solid rgba(0,0,0,0.15)' }}>
                  <RotateCcw size={14} /> Reset defaults
                </button>
                <button onClick={save} data-testid="permissions-save-btn" className="flex items-center gap-1.5 px-4 py-2 rounded text-sm font-title transition-colors" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>
                  <Save size={14} /> Save changes
                </button>
              </div>
            </div>
            {saved && <p className="text-sm mb-3" style={{ color: 'var(--navy)' }}>Permissions saved.</p>}

            {/* Member Classes manager */}
            <div className="bg-white rounded-lg shadow-sm border p-4 mb-4" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="member-classes-panel">
              <h3 className="flex items-center gap-2 font-title text-lg mb-1" style={{ color: 'var(--navy)' }}>
                <Users size={18} /> Member Classes
              </h3>
              <p className="text-xs mb-3" style={{ color: 'var(--olive)' }}>All classes appear as columns below. Built-in classes can be renamed; custom classes you add can be renamed or deleted. Deleting a class moves its members to “Contact”.</p>
              <div className="flex flex-wrap gap-2 mb-3" data-testid="member-classes-list">
                {memberClasses.map((mc) => (
                  <div key={mc.key} data-testid={`member-class-${mc.key}`} className="flex items-center gap-1.5 rounded px-2.5 py-1.5" style={{ background: mc.builtin ? 'var(--sage)' : 'var(--gold)', color: '#3a2c06' }}>
                    {editingClassKey === mc.key ? (
                      <>
                        <input autoFocus value={editingClassLabel} onChange={(e) => setEditingClassLabel(e.target.value)} className="px-1.5 py-0.5 rounded text-sm w-32" style={{ border: '1px solid rgba(0,0,0,0.25)' }} />
                        <button data-testid={`member-class-save-${mc.key}`} disabled={classBusy} onClick={async () => { if (!editingClassLabel.trim()) return; setClassBusy(true); try { await renameMemberClass(mc.key, editingClassLabel.trim()); setEditingClassKey(null); } catch (e) { window.alert('Could not rename class.'); } setClassBusy(false); }}><Check size={14} /></button>
                        <button onClick={() => setEditingClassKey(null)}><X size={14} /></button>
                      </>
                    ) : (
                      <>
                        <span className="text-sm font-title">{mc.label}</span>
                        {mc.builtin && <span className="text-[9px] uppercase tracking-wide opacity-70">built-in</span>}
                        <button data-testid={`member-class-rename-${mc.key}`} title="Rename" onClick={() => { setEditingClassKey(mc.key); setEditingClassLabel(mc.label); }}><Pencil size={12} /></button>
                        {!mc.builtin && (
                          <button data-testid={`member-class-delete-${mc.key}`} title="Delete" onClick={async () => { if (!window.confirm(`Delete class “${mc.label}”? Members in it move to Contact.`)) return; setClassBusy(true); try { await deleteMemberClass(mc.key); } catch (e) { window.alert('Could not delete class.'); } setClassBusy(false); }}><Trash2 size={12} /></button>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Input data-testid="new-class-input" value={newClassLabel} onChange={(e) => setNewClassLabel(e.target.value)} placeholder="New class name, e.g. Honorary" className="h-9 max-w-xs"
                  onKeyDown={async (e) => { if (e.key === 'Enter' && newClassLabel.trim() && !classBusy) { setClassBusy(true); try { await addMemberClass(newClassLabel.trim()); setNewClassLabel(''); } catch (err) { window.alert('Could not add class.'); } setClassBusy(false); } }} />
                <button data-testid="add-class-btn" disabled={classBusy || !newClassLabel.trim()} onClick={async () => { setClassBusy(true); try { await addMemberClass(newClassLabel.trim()); setNewClassLabel(''); } catch (e) { window.alert('Could not add class.'); } setClassBusy(false); }}
                  className="flex items-center gap-1 px-3 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: classBusy || !newClassLabel.trim() ? 0.6 : 1 }}>
                  <Plus size={15} /> Add class
                </button>
              </div>
            </div>

            <div className="overflow-x-auto bg-white rounded-lg shadow-sm border" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: 'var(--navy)' }}>
                    <th className="px-4 py-3 text-left font-title sticky left-0" style={{ color: 'var(--cream)', background: 'var(--navy)' }}>Page</th>
                    {memberClasses.map((mc) => (
                      <th key={mc.key} className="px-3 py-3 text-center font-title" style={{ color: 'var(--cream)' }}>{mc.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pages.map((p, i) => (
                    <tr key={p.key} style={{ background: i % 2 ? 'rgba(0,0,0,0.025)' : 'white' }}>
                      <td className="px-4 py-2.5 sticky left-0" style={{ background: i % 2 ? '#eceee8' : 'white' }}>{p.label}</td>
                      {memberClasses.map((mc) => {
                        const c = mc.key;
                        const checked = (draft[p.key] || []).includes(c);
                        return (
                          <td key={c} className="px-3 py-2.5 text-center">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggle(p.key, c)}
                              className="w-4 h-4 cursor-pointer accent-[var(--maroon)]"
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Admin sections access */}
            <div className="mt-6">
              <h3 className="flex items-center gap-2 font-title text-lg mb-1" style={{ color: 'var(--navy)' }}>
                <ShieldCheck size={18} /> Admin sections
              </h3>
              <p className="text-xs mb-3" style={{ color: 'var(--olive)' }}>Tick which classes may open each Admin section (it then appears in their Admin menu). Note: some sections also need their server actions opened to that class — ask if you want a section fully delegated to a non-admin class.</p>
              <div className="overflow-x-auto bg-white rounded-lg shadow-sm border" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: 'var(--navy)' }}>
                      <th className="px-4 py-3 text-left font-title sticky left-0" style={{ color: 'var(--cream)', background: 'var(--navy)' }}>Admin section</th>
                      {memberClasses.map((mc) => (
                        <th key={mc.key} className="px-3 py-3 text-center font-title" style={{ color: 'var(--cream)' }}>{mc.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ADMIN_SECTIONS.map((s, i) => (
                      <tr key={s.perm} data-testid={`perm-row-${s.perm}`} style={{ background: i % 2 ? 'rgba(0,0,0,0.025)' : 'white' }}>
                        <td className="px-4 py-2.5 sticky left-0" style={{ background: i % 2 ? '#eceee8' : 'white' }}>{s.label}</td>
                        {memberClasses.map((mc) => (
                          <td key={mc.key} className="px-3 py-2.5 text-center">
                            <input type="checkbox" checked={(draft[s.perm] || []).includes(mc.key)} onChange={() => toggle(s.perm, mc.key)}
                              data-testid={`perm-${s.perm}-${mc.key}`} className="w-4 h-4 cursor-pointer accent-[var(--maroon)]" />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === 'members' && (
          <div>
            {/* Tag toolbar: filter + bulk selecting */}
            <div className="bg-white rounded-lg shadow-sm border p-4 mb-4" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="tag-toolbar">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-sm font-title" style={{ color: 'var(--navy)' }}>Filter:</span>
                <TagFilter tags={allTags.map((t) => ({ name: t, count: users.filter((u) => (u.tags || []).includes(t)).length }))} value={tagFilter} onChange={setTagFilter} exclude={tagExclude} onExcludeChange={setTagExclude} match={tagMatch} onMatchChange={setTagMatch} testid="tag-filter" />
                <span className="text-xs" style={{ color: 'var(--olive)' }} data-testid="tag-filter-count">{tagFilter.length || tagExclude.length ? `${filteredUsers.length} of ${users.length}` : `All (${users.length})`}</span>
                {allTags.length === 0 && <span className="text-xs" style={{ color: 'var(--olive)' }}>No tags in use yet — add tags to members below.</span>}
                <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="ml-auto text-xs rounded border px-2 py-1" style={{ borderColor: roleFilter ? 'var(--maroon)' : 'rgba(0,0,0,0.2)' }} data-testid="member-role-filter">
                  <option value="">Any role / none</option>
                  <option value="__any">Current office bearers</option>
                  {allRoles.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="flex flex-wrap items-start gap-2">
                <span className="text-sm mt-2" style={{ color: 'var(--olive)' }}>{selectedIds.length} selected</span>
                <div className="min-w-[240px] flex-1">
                  <TagMultiSelect value={bulkTags} onChange={setBulkTags} testid="bulk-tag-input" />
                </div>
                <button onClick={async () => { if (!selectedIds.length) { toast.error('Tick the members first.'); return; } try { await requestDetailsUpdate(selectedIds); setCommsPrefill({ subject: 'Please check your membership details', body: 'Dear {{first_name}},\n\nTo keep our records accurate, could you please take a moment to check your contact details and let us know of any changes?\n\nOpen your details page here: {{details_link}}\n\nOnce everything is correct, click "My details are correct" so we know you have checked.\n\nThank you — Once a Sapper, Always a Sapper.' }); openComms(''); } catch { toast.error('Could not flag the members.'); } }} data-testid="request-details-btn"
                  className="px-3 py-2 rounded text-sm font-title inline-flex items-center gap-1.5" style={{ background: 'var(--navy)', color: 'var(--cream)' }}>
                  Request details update{selectedIds.length ? ` (${selectedIds.length})` : ''}
                </button>
                <button onClick={() => { setCommsPrefill(null); openComms(''); }} data-testid="comms-open"
                  className="px-3 py-2 rounded text-sm font-title inline-flex items-center gap-1.5" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>
                  <Send size={14} /> Send communication{selectedIds.length ? ` (${selectedIds.length})` : ''}
                </button>
                <Select value="none" onValueChange={(v) => { if (v !== 'none') openComms(v === 'free' ? '' : v); }}>
                  <SelectTrigger className="w-64 h-9" data-testid="comms-template-picker" disabled={!selectedIds.length}>
                    <SelectValue placeholder="Apply a message/template…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" disabled>{selectedIds.length ? `Message the ${selectedIds.length} selected…` : 'Select members first'}</SelectItem>
                    <SelectItem value="free" data-testid="comms-template-option-free">Free text message</SelectItem>
                    {msgTemplates.map((t) => <SelectItem key={t.slug} value={t.slug} data-testid={`comms-template-option-${t.slug}`}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button onClick={() => applyBulkTag('add')} disabled={!bulkTags.length || !selectedIds.length} data-testid="bulk-tag-add"
                  className="px-3 py-2 rounded text-sm font-title" style={{ background: 'var(--navy)', color: 'var(--cream)', opacity: (!bulkTags.length || !selectedIds.length) ? 0.6 : 1 }}>
                  Tag selected
                </button>
                <button onClick={() => applyBulkTag('remove')} disabled={!bulkTags.length || !selectedIds.length} data-testid="bulk-tag-remove"
                  className="px-3 py-2 rounded text-sm font-title" style={{ background: 'white', border: '1px solid rgba(0,0,0,0.15)', opacity: (!bulkTags.length || !selectedIds.length) ? 0.6 : 1 }}>
                  Untag selected
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
                <span className="text-sm font-title" style={{ color: 'var(--navy)' }}>CSV:</span>
                <button onClick={exportCsv} data-testid="csv-export-btn" className="px-3 py-2 rounded text-sm font-title" style={{ background: 'var(--olive)', color: 'var(--cream)' }}>
                  Export {tagFilter.length || tagExclude.length ? 'filtered' : 'all'} ({(tagFilter.length || tagExclude.length ? filteredUsers : users).length})
                </button>
                <input ref={csvFileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onImportFile} data-testid="csv-import-input" />
                <button onClick={() => csvFileRef.current && csvFileRef.current.click()} data-testid="csv-import-btn" className="px-3 py-2 rounded text-sm font-title" style={{ background: 'var(--navy)', color: 'var(--cream)' }}>
                  Import CSV
                </button>
                <span className="text-xs" style={{ color: 'var(--olive)' }}>Columns: name, email, member_class, rank, unit, years_served, tags (tags separated by ;). Rows are matched/updated by email.</span>
                <input ref={sheetFileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onImportSheet} data-testid="sheet-import-input" />
                <button onClick={() => sheetFileRef.current && sheetFileRef.current.click()} data-testid="sheet-import-btn" className="px-3 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>
                  Import RAE spreadsheet
                </button>
                <span className="text-xs" style={{ color: 'var(--olive)' }}>For the members spreadsheet layout (LastName, FirstName, Address, Suburb, Phone No, Mobile, Email, Status, Note, year columns, DateLastPaid). Export the sheet as CSV first.</span>
                <button onClick={syncFromGoogleSheet} disabled={sheetSyncBusy} data-testid="sheet-sync-btn" className="px-3 py-2 rounded text-sm font-title inline-flex items-center gap-1.5" style={{ background: 'var(--gold)', color: '#3a2c06', opacity: sheetSyncBusy ? 0.6 : 1 }}>
                  <RefreshCw size={14} className={sheetSyncBusy ? 'animate-spin' : ''} /> Sync with Google Sheet
                </button>
                <span className="text-xs" style={{ color: 'var(--olive)' }}>Pulls the latest member list live from the linked Google Sheet (set up in Google Workspace). Matched/updated by email.</span>
                {importMsg && <span className="text-sm w-full" data-testid="csv-import-msg" style={{ color: 'var(--navy)' }}>{importMsg}</span>}
              </div>
            </div>

            <div className="mb-3">
              <EmailHealthCard selectedIds={selectedIds} displayedIds={sortedUsers.map((u) => u.user_id)} />
            </div>
            <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
              <TableSearch query={memberQuery} setQuery={setMemberQuery} placeholder="Search name, rank, unit, email, tag…" testid="members-search" />
              <Link to="/admin/contacts/new" className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm font-title" style={{ background: 'var(--navy)', color: 'var(--cream)' }} data-testid="members-new-contact"><UserPlus size={14} /> New contact</Link>
              <div className="flex flex-wrap items-center gap-1.5" data-testid="email-health-filter">
                <span className="text-xs" style={{ color: 'var(--olive)' }}>Email:</span>
                {[['all', 'All'], ['problems', 'Problems'], ['unchecked', 'Not checked'], ['none', 'No email']].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setHealthFilter(v)} data-testid={`email-health-filter-${v}`} className="px-2 py-0.5 rounded text-[11px] font-title"
                    style={healthFilter === v ? { background: v === 'problems' ? '#8a1f11' : 'var(--navy)', color: 'var(--cream)' } : { background: 'white', border: '1px solid rgba(0,0,0,0.18)' }}>{l}</button>
                ))}
              </div>
              <span className="text-xs" style={{ color: 'var(--olive)' }}>{sortedUsers.length} shown</span>
            </div>
            <div className="bg-white rounded-lg shadow-sm border overflow-x-auto" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: 'var(--navy)' }}>
                  <th className="px-3 py-3"><input type="checkbox" checked={allSelected} onChange={toggleSelectAll} title="Select all displayed members" data-testid="select-all" className="w-4 h-4 accent-[var(--gold)]" /></th>
                  <SortTh label="Name" sortKey="name" get={(u) => u.name} sort={memberSort} toggle={toggleMemberSort} className="px-4 py-3 text-left font-title" style={{ color: 'var(--cream)' }} testid="sort-member-name" />
                  <SortTh label="Email" sortKey="email" get={(u) => u.email || ''} sort={memberSort} toggle={toggleMemberSort} className="px-3 py-3 text-left font-title" style={{ color: 'var(--cream)' }} testid="sort-member-email" />
                  <SortTh label="Phone" sortKey="phone" get={(u) => u.phone || ''} sort={memberSort} toggle={toggleMemberSort} className="px-3 py-3 text-left font-title" style={{ color: 'var(--cream)' }} testid="sort-member-phone" />
                  <SortTh label="Mobile" sortKey="mobile" get={(u) => u.mobile || ''} sort={memberSort} toggle={toggleMemberSort} className="px-3 py-3 text-left font-title" style={{ color: 'var(--cream)' }} testid="sort-member-mobile" />
                  <SortTh label="Address" sortKey="suburb" get={(u) => `${u.suburb || ''} ${u.address || ''}`} sort={memberSort} toggle={toggleMemberSort} className="px-3 py-3 text-left font-title" style={{ color: 'var(--cream)' }} testid="sort-member-address" />
                  <SortTh label="Status" sortKey="status" get={(u) => u.status || ''} sort={memberSort} toggle={toggleMemberSort} className="px-3 py-3 text-left font-title" style={{ color: 'var(--cream)' }} testid="sort-member-status" />
                  <SortTh label="Last paid" sortKey="date_last_paid" get={(u) => u.date_last_paid || ''} sort={memberSort} toggle={toggleMemberSort} className="px-3 py-3 text-left font-title" style={{ color: 'var(--cream)' }} testid="sort-member-paid" />
                  <SortTh label="Joined" sortKey="date_joined" get={(u) => u.date_joined || ''} sort={memberSort} toggle={toggleMemberSort} className="px-3 py-3 text-left font-title" style={{ color: 'var(--cream)' }} testid="sort-member-joined" />
                  <SortTh label="Current role" sortKey="role" get={(u) => currentRoles(u).join(', ')} sort={memberSort} toggle={toggleMemberSort} className="px-3 py-3 text-left font-title" style={{ color: 'var(--cream)' }} testid="sort-member-role" />
                  <SortTh label="Rank" sortKey="rank" get={(u) => u.rank || ''} sort={memberSort} toggle={toggleMemberSort} className="px-4 py-3 text-left font-title" style={{ color: 'var(--cream)' }} testid="sort-member-rank" />
                  <SortTh label="Unit" sortKey="unit" get={(u) => u.unit || ''} sort={memberSort} toggle={toggleMemberSort} className="px-4 py-3 text-left font-title" style={{ color: 'var(--cream)' }} testid="sort-member-unit" />
                  <SortTh label="Years" sortKey="years_served" get={(u) => u.years_served || ''} sort={memberSort} toggle={toggleMemberSort} className="px-4 py-3 text-left font-title" style={{ color: 'var(--cream)' }} testid="sort-member-years" />
                  <SortTh label="Member Class" sortKey="memberClass" get={(u) => classLabel(u.memberClass)} sort={memberSort} toggle={toggleMemberSort} className="px-4 py-3 text-left font-title" style={{ color: 'var(--cream)' }} testid="sort-member-class" />
                  <th className="px-3 py-3 text-left font-title" style={{ color: 'var(--cream)' }}>Note</th>
                  <th className="px-4 py-3 text-left font-title" style={{ color: 'var(--cream)' }}>Tags</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((u, i) => (
                  <tr key={u.user_id} data-testid={`member-row-${u.user_id}`} style={{ background: i % 2 ? 'rgba(0,0,0,0.025)' : 'white' }}>
                    <td className="px-3 py-2.5 text-center">
                      <input type="checkbox" checked={selectedIds.includes(u.user_id)} onChange={() => toggleSelect(u.user_id)} data-testid={`select-${u.user_id}`} className="w-4 h-4 accent-[var(--maroon)]" />
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="whitespace-nowrap">{u.name}</div>
                      {(u.first_name || u.last_name) && u.name !== `${u.first_name || ''} ${u.last_name || ''}`.trim() && (
                        <div className="text-xs" style={{ color: 'var(--olive)' }}>{`${u.first_name || ''} ${u.last_name || ''}`.trim()}</div>
                      )}
                      <div className="flex flex-wrap gap-x-3">
                      <button onClick={() => setEditUser(u)}
                        className="mt-1 inline-flex items-center gap-1 text-xs underline" style={{ color: 'var(--maroon)', fontWeight: 600 }}
                        data-testid={`edit-member-${u.user_id}`}>
                        <Pencil size={12} /> Edit
                      </button>
                      <button onClick={() => { setPwUser(u); setPwValue(''); setPwMsg(''); }}
                        className="mt-1 inline-flex items-center gap-1 text-xs underline" style={{ color: 'var(--navy)' }}
                        data-testid={`set-password-${u.user_id}`}>
                        <KeyRound size={12} /> Set password
                      </button>
                      <button onClick={() => openActivity(u)}
                        className="mt-1 inline-flex items-center gap-1 text-xs underline" style={{ color: 'var(--navy)' }}
                        data-testid={`view-activity-${u.user_id}`}>
                        <Activity size={12} /> Activity
                      </button>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-xs" data-testid={`member-email-${u.user_id}`}>
                      {u.email ? <a href={`mailto:${u.email}`} className="underline" style={{ color: 'var(--navy)' }}>{u.email}</a> : <span style={{ color: 'var(--olive)' }}>—</span>}
                      <div><EmailHealthBadge user={u} /></div>
                    </td>
                    <td className="px-3 py-2.5 text-xs whitespace-nowrap" data-testid={`member-phone-${u.user_id}`}>{u.phone || <span style={{ color: 'var(--olive)' }}>—</span>}</td>
                    <td className="px-3 py-2.5 text-xs whitespace-nowrap" data-testid={`member-mobile-${u.user_id}`}>{u.mobile || <span style={{ color: 'var(--olive)' }}>—</span>}</td>
                    <td className="px-3 py-2.5 text-xs" data-testid={`member-address-${u.user_id}`}>
                      {u.address && <div>{u.address}</div>}
                      <div style={{ color: u.address ? 'var(--olive)' : undefined }}>{[u.suburb, u.postcode].filter(Boolean).join(' ') || (!u.address && <span style={{ color: 'var(--olive)' }}>—</span>)}</div>
                    </td>
                    <td className="px-3 py-2.5 text-xs whitespace-nowrap" data-testid={`member-status-${u.user_id}`}>{u.status || <span style={{ color: 'var(--olive)' }}>—</span>}</td>
                    <td className="px-3 py-2.5 text-xs whitespace-nowrap" data-testid={`member-paid-${u.user_id}`}>{u.date_last_paid || <span style={{ color: 'var(--olive)' }}>—</span>}</td>
                    <td className="px-3 py-2.5 text-xs whitespace-nowrap" data-testid={`member-joined-${u.user_id}`}>{u.date_joined || <span style={{ color: 'var(--olive)' }}>—</span>}</td>
                    <td className="px-3 py-2.5 text-xs" data-testid={`member-role-${u.user_id}`}>{currentRoles(u).length ? currentRoles(u).map((r) => <span key={r} className="inline-block mr-1 mb-0.5 px-1.5 py-0.5 rounded-full text-[10px]" style={{ background: '#e6f4ea', color: '#2e7d32' }}>{r}</span>) : <span style={{ color: 'var(--olive)' }}>—</span>}</td>
                    <td className="px-3 py-2.5">
                      <Input defaultValue={u.rank || ''} placeholder="e.g. Cpl" className="w-24 h-8"
                        onBlur={(e) => { const v = e.target.value; if (v !== (u.rank || '')) updateUserFields(u.user_id, { rank: v }); }} />
                    </td>
                    <td className="px-3 py-2.5">
                      <Input defaultValue={u.unit || ''} placeholder="e.g. 12 Fd Sqn" className="w-36 h-8"
                        onBlur={(e) => { const v = e.target.value; if (v !== (u.unit || '')) updateUserFields(u.user_id, { unit: v }); }} />
                    </td>
                    <td className="px-3 py-2.5">
                      <Input defaultValue={u.years_served || ''} placeholder="e.g. 1978-1985" className="w-28 h-8"
                        onBlur={(e) => { const v = e.target.value; if (v !== (u.years_served || '')) updateUserFields(u.user_id, { years_served: v }); }} />
                    </td>
                    <td className="px-4 py-2.5">
                      <Select value={u.memberClass} onValueChange={(v) => changeUserClass(u.user_id, v)}>
                        <SelectTrigger className="w-44" data-testid={`member-class-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {memberClasses.map((mc) => (
                            <SelectItem key={mc.key} value={mc.key}>{mc.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2.5 text-xs max-w-[200px]" data-testid={`member-note-${u.user_id}`} title={u.note || ''}>
                      <div className="line-clamp-2">{u.note || <span style={{ color: 'var(--olive)' }}>—</span>}</div>
                    </td>
                    <td className="px-4 py-2.5" data-testid={`member-tags-${u.user_id}`}>
                      <div className="flex flex-wrap items-center gap-1 max-w-[220px]">
                        {(u.tags || []).map((t) => (
                          <span key={t} className="flex items-center gap-1 px-2 py-0.5 rounded text-xs" style={t.startsWith('Role: ') ? { background: 'var(--navy)', color: 'var(--cream)' } : { background: 'var(--sage)', color: '#3a2c06' }} title={t.startsWith('Role: ') ? 'Set automatically from this member\'s current appointments' : undefined} data-testid={t.startsWith('Role: ') ? `role-tag-${u.user_id}` : undefined}>
                            {t}
                            {!t.startsWith('Role: ') && <button onClick={() => removeRowTag(u.user_id, t)} title="Remove tag" data-testid={`tag-remove-${u.user_id}-${t}`}><X size={11} /></button>}
                          </span>
                        ))}
                        <input
                          value={rowTag[u.user_id] || ''}
                          onChange={(e) => setRowTag((r) => ({ ...r, [u.user_id]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') addRowTag(u.user_id); }}
                          placeholder="+ tag"
                          data-testid={`tag-input-${u.user_id}`}
                          className="w-16 px-1.5 py-0.5 rounded border text-xs"
                          style={{ borderColor: 'rgba(0,0,0,0.2)' }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs px-4 py-2" style={{ color: 'var(--olive)' }}>Rank &amp; Unit appear in the members-only directory. Type a tag and press Enter to add it. Use tags to group contacts, then filter or bulk-tag selected rows.</p>
            </div>
          </div>
        )}

        {tab === 'pages' && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Menu tree */}
            <div>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="flex items-center gap-2 font-title text-lg" style={{ color: 'var(--navy)' }}>
                  <ListTree size={18} /> Site Menu Tree
                </h3>
                <div className="flex gap-2">
                  <button data-testid="menu-add-btn" onClick={addTopLevel} className="flex items-center gap-1 px-3 py-1.5 rounded text-sm font-title" style={{ background: 'var(--navy)', color: 'var(--cream)' }}>
                    <Plus size={14} /> Add page
                  </button>
                  <button data-testid="menu-add-group-btn" onClick={addGroup} className="flex items-center gap-1 px-3 py-1.5 rounded text-sm font-title" style={{ background: 'var(--olive)', color: 'var(--cream)' }}>
                    <Plus size={14} /> Add group
                  </button>
                  <button data-testid="menu-reset-btn" onClick={resetMenu} className="flex items-center gap-1 px-3 py-1.5 rounded text-sm font-title" style={{ background: 'white', border: '1px solid rgba(0,0,0,0.15)' }}>
                    <RotateCcw size={13} /> Reset
                  </button>
                  <button data-testid="menu-save-btn" onClick={saveMenu} className="flex items-center gap-1 px-4 py-1.5 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>
                    <Save size={14} /> Save order
                  </button>
                </div>
              </div>
              <p className="text-xs mb-3" style={{ color: 'var(--olive)' }}>
                Drag a row to reorder it within its list (or use the arrows / focus a row and press ↑/↓). Drag whole groups to reorder the top-level menu. Move pages between menu groups, hide/show any item, mark a page as <strong>Draft</strong> (optionally scheduled) to hide it from members, and use <strong>+</strong> to add a page. Every page can be previewed, moved, hidden, edited, duplicated, or deleted.
              </p>
              <div className="flex items-center gap-2 mb-3 text-sm" data-testid="preview-as-toolbar">
                <span style={{ color: 'var(--olive)' }}>Preview pages as:</span>
                <select
                  data-testid="preview-as-class"
                  value={previewAsClass}
                  onChange={(e) => setPreviewAsClass(e.target.value)}
                  className="rounded border px-2 py-1 text-sm"
                  style={{ borderColor: 'rgba(0,0,0,0.2)' }}
                >
                  {memberClasses.filter((mc) => mc.key !== 'admin').map((mc) => (
                    <option key={mc.key} value={mc.key}>{mc.label}</option>
                  ))}
                </select>
                <span style={{ color: 'var(--olive)' }}>then click the <Eye size={12} className="inline" /> on a page.</span>
              </div>
              {menuSaved && <p className="text-sm mb-3" style={{ color: 'var(--navy)' }} data-testid="menu-saved-msg">Menu order saved and applied.</p>}
              <div className="space-y-2" data-testid="menu-tree">
                {overviewEdit && (
                  <GroupOverviewEditor
                    key={overviewEdit.id}
                    node={overviewEdit}
                    open
                    onClose={() => setOverviewEdit(null)}
                    onApply={(patch) => setGroupMeta(overviewEdit.id, patch)}
                  />
                )}
                {(function renderTree(nodes, depth) {
                  return nodes.map((node) => (
                    <div key={node.id} className="space-y-2">
                      {renderMenuRow(node, depth, depth > 0)}
                      {node.kind === 'group' && renderTree(node.children || [], depth + 1)}
                    </div>
                  ));
                })(menuDraft, 0)}
              </div>

              {(customPages || []).filter((p) => p.parent === 'none').length > 0 && (
                <div className="mt-5" data-testid="hidden-pages">
                  <h4 className="font-title text-sm mb-1" style={{ color: 'var(--olive)' }}>Hidden pages (direct link only)</h4>
                  <p className="text-xs mb-2" style={{ color: 'var(--olive)' }}>These pages are not shown in the menu but are still live at their link. Edit or delete them here.</p>
                  <div className="space-y-2">
                    {(customPages || []).filter((p) => p.parent === 'none').map((p) => (
                      <div key={p.page_key} data-testid={`hidden-page-${p.page_key}`} className="flex items-center gap-2 rounded border px-2 py-2 bg-white" style={{ borderColor: 'rgba(0,0,0,0.14)' }}>
                        <span className="text-sm flex-1 truncate">{p.nav_label || p.title}</span>
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-title" style={{ background: '#eee', color: '#666' }}>Hidden</span>
                        {p.draft && <span className="px-1.5 py-0.5 rounded text-[10px]" style={{ background: '#fff4e5', color: '#8a5a00' }}>Draft</span>}
                        <button onClick={() => openPreview(p.path)} title={`Preview as ${classLabel(previewAsClass)}`} data-testid={`hidden-preview-${p.page_key}`} className="p-1 rounded" style={{ background: 'var(--gold)', color: '#3a2c06' }}><Eye size={13} /></button>
                        <button onClick={() => duplicatePage(p.page_key)} title="Duplicate" data-testid={`hidden-duplicate-${p.page_key}`} className="p-1 rounded" style={{ background: 'var(--olive)', color: 'var(--cream)' }}><Copy size={13} /></button>
                        <button onClick={() => startEdit(p)} title="Edit" className="p-1 rounded" style={{ background: 'var(--navy)', color: 'var(--cream)' }}><Pencil size={13} /></button>
                        <button onClick={() => removePage(p)} title="Delete" className="p-1 rounded" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}><Trash2 size={13} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Add / edit form */}
            <div data-testid="page-edit-form" className="bg-white rounded-lg p-5 shadow-sm border" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-title text-lg" style={{ color: 'var(--navy)' }}>
                  {editingKey ? 'Edit Page' : 'Add New Page'}
                </h3>
                {editingKey && (
                  <button onClick={startNew} className="flex items-center gap-1 text-sm" style={{ color: 'var(--olive)' }}><X size={14} /> New instead</button>
                )}
              </div>
              {editingKey && editingPage && editingPage.last_edited_by && (
                <p className="text-xs mb-3 -mt-1" data-testid="page-last-edited" style={{ color: 'var(--olive)' }}>
                  Last edited by <strong>{editingPage.last_edited_by}</strong> · {relTime(editingPage.last_edited_at)}
                </p>
              )}
              <div className="space-y-3">
                <div>
                  <label className="text-sm block mb-1">Page title</label>
                  <Input data-testid="page-form-title" value={pageForm.title} onChange={(e) => setPageForm({ ...pageForm, title: e.target.value })} placeholder="e.g. Newsletter" />
                </div>
                <div>
                  <label className="text-sm block mb-1">Menu label (optional)</label>
                  <Input value={pageForm.nav_label} onChange={(e) => setPageForm({ ...pageForm, nav_label: e.target.value })} placeholder="Defaults to title" />
                </div>
                <div>
                  <label className="text-sm block mb-1">Menu placement</label>
                  <Select value={pageForm.parent} onValueChange={(v) => setPageForm({ ...pageForm, parent: v })}>
                    <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {parentOptions.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                {!editingBuiltin ? (
                  <>
                    <div>
                      <label className="text-sm block mb-1">Intro (optional)</label>
                      <Input value={pageForm.intro} onChange={(e) => setPageForm({ ...pageForm, intro: e.target.value })} />
                    </div>
                    <div>
                      <label className="text-sm block mb-1">Body content</label>
                      <Textarea rows={6} value={pageForm.body} onChange={(e) => setPageForm({ ...pageForm, body: e.target.value })} data-testid="page-body-editor" placeholder="Write paragraphs. Separate paragraphs with a blank line." />
                    </div>
                    <div>
                      <label className="text-sm block mb-1 flex items-center gap-2">
                        Raw HTML <span className="text-xs" style={{ color: 'var(--olive)' }}>(optional — overrides Body when set; full HTML/CSS allowed)</span>
                      </label>
                      <Textarea rows={8} value={pageForm.html} onChange={(e) => setPageForm({ ...pageForm, html: e.target.value })} placeholder="<h2>Heading</h2><p>Custom HTML…</p>" data-testid="page-html-editor" style={{ fontFamily: 'monospace', fontSize: '13px' }} />
                      {pageForm.html && (
                        <div className="mt-2 p-3 rounded border" style={{ borderColor: 'rgba(0,0,0,0.12)', background: 'rgba(0,0,0,0.02)' }}>
                          <div className="text-xs mb-1" style={{ color: 'var(--olive)' }}>Live preview</div>
                          <div className="page-html" data-testid="page-html-preview" dangerouslySetInnerHTML={{ __html: sanitizeHtml(pageForm.html) }} />
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="rounded p-3 text-sm" data-testid="builtin-edit-note" style={{ background: '#eef3f7', border: '1px solid #cdd9e4', color: 'var(--navy)' }}>
                    This page has a rich layout (headings, tables, images, lists). Edit its wording <strong>directly on the page</strong> — right-click any text or image to change it. Use the fields here to rename it, move it in the menu, set who can view it, or delete it.
                    {editingComponent ? (
                      <p className="mt-2 text-xs" style={{ color: 'var(--olive)' }}>This page uses a built-in layout component and can’t be converted to a plain text page.</p>
                    ) : (
                      <button onClick={() => convertBuiltin(editingKey)} disabled={pageBusy} data-testid="convert-builtin-btn" className="mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-title" style={{ background: 'var(--navy)', color: 'var(--cream)' }}>
                        <FileText size={13} /> Convert to a normal editable page
                      </button>
                    )}
                  </div>
                )}
                <div>
                  <label className="text-sm block mb-2">Who can view this page?</label>
                  <div className="flex flex-wrap gap-2">
                    {memberClasses.map((mc) => {
                      const c = mc.key;
                      const on = pageForm.allowed_classes.includes(c);
                      return (
                        <button key={c} onClick={() => togglePageClass(c)} data-testid={`page-view-class-${c}`} className="px-2.5 py-1 rounded text-xs font-title" style={on ? { background: 'var(--navy)', color: 'var(--cream)' } : { background: 'white', color: 'var(--olive)', border: '1px solid rgba(0,0,0,0.15)' }}>
                          {mc.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div data-testid="page-editors-block">
                  <label className="text-sm block mb-1">Who can edit this page content?</label>
                  <p className="text-xs mb-2" style={{ color: 'var(--olive)' }}>Beyond viewing — these roles and people can change the page text (System &amp; Site Admins always can).</p>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {memberClasses.filter((mc) => mc.key !== 'admin').map((mc) => {
                      const on = (pageForm.edit_classes || []).includes(mc.key);
                      return (
                        <button key={mc.key} onClick={() => togglePageEditClass(mc.key)} data-testid={`page-edit-class-${mc.key}`} className="px-2.5 py-1 rounded text-xs font-title" style={on ? { background: 'var(--maroon)', color: 'var(--cream)' } : { background: 'white', color: 'var(--olive)', border: '1px solid rgba(0,0,0,0.15)' }}>
                          {mc.label}
                        </button>
                      );
                    })}
                  </div>
                  <Input
                    data-testid="page-edit-users"
                    value={(pageForm.edit_users || []).join(', ')}
                    onChange={(e) => setPageForm({ ...pageForm, edit_users: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
                    placeholder="Individual editors by email, comma-separated"
                  />
                </div>
                <label className="flex items-center justify-between gap-3 text-sm py-1" data-testid="page-draft-toggle">
                  <span>Draft (hidden from members until published)</span>
                  <Switch checked={!!pageForm.draft} onCheckedChange={(v) => setPageForm({ ...pageForm, draft: v })} />
                </label>
                {pageForm.draft && (
                  <div>
                    <label className="text-sm block mb-1">Schedule publish (optional)</label>
                    <input
                      type="datetime-local"
                      data-testid="page-publish-at"
                      value={pageForm.publish_at}
                      onChange={(e) => setPageForm({ ...pageForm, publish_at: e.target.value })}
                      className="w-full px-3 py-2 rounded border text-sm"
                      style={{ borderColor: 'rgba(0,0,0,0.2)' }}
                    />
                    <p className="text-xs mt-1" style={{ color: 'var(--olive)' }}>Leave empty to keep it a draft until you publish manually. If set, the page goes live automatically at this time.</p>
                  </div>
                )}
                <button onClick={submitPage} disabled={pageBusy || !pageForm.title.trim()} data-testid="page-form-submit" className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded font-title transition-colors" style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: pageBusy ? 0.7 : 1 }}>
                  {editingKey ? <Save size={16} /> : <Plus size={16} />} {editingKey ? 'Save changes' : 'Create page'}
                </button>
                {editingKey && <ObjectHistory type="page" id={editingKey} refreshKey={pageBusy} />}
                {editingKey && !editingComponent && (
                  <button onClick={() => window.open(`/admin/pages/builder/${editingKey}`, '_blank', 'noopener')} data-testid="page-builder-btn" className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded font-title text-sm" style={{ background: 'var(--navy)', color: 'var(--cream)' }}>
                    <LayoutTemplate size={16} /> Design layout with modules
                    {editingPage && Array.isArray(editingPage.widgets) && editingPage.widgets.length > 0 && (
                      <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'var(--gold)', color: 'var(--ink)' }}>{editingPage.widgets.length}</span>
                    )}
                  </button>
                )}
                {editingKey && (
                  <button onClick={() => { const p = customByKey(editingKey); if (p) removePage(p); }} disabled={pageBusy} data-testid="page-delete-btn" className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded font-title text-sm" style={{ background: 'white', color: 'var(--maroon)', border: '1px solid var(--maroon)' }}>
                    <Trash2 size={15} /> Delete this page
                  </button>
                )}
              </div>
            </div>
          </div>
        )}


        {tab === 'email' && (
          <div className="grid lg:grid-cols-2 gap-6" data-testid="email-group-panel">
            <div className="lg:col-span-2"><BirthdayGreetingsCard /></div>
            <div className="bg-white rounded-lg shadow-sm border p-5" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <h3 className="flex items-center gap-2 font-title text-lg mb-1" style={{ color: 'var(--navy)' }}>
                <Send size={18} /> Email a group
              </h3>
              <p className="text-sm mb-3" style={{ color: 'var(--olive)' }}>Pick one or more tags, write your message, and send it to everyone in that group in one click. Use <code>{'{{name}}'}</code> to personalise.</p>

              {!emailConfigured && (
                <div className="rounded p-3 mb-4 text-sm" data-testid="email-mock-banner" style={{ background: '#fff4e5', border: '1px solid #e0b070', color: '#8a5a00' }}>
                  <strong>Demo mode:</strong> No email provider is connected, so messages are logged but not actually delivered. Connect Resend or SMTP in Email Settings to send for real.
                </div>
              )}

              <label className="text-sm block mb-1" style={{ color: 'var(--navy)' }}>Tags</label>
              {allTags.length === 0 ? (
                <p className="text-sm mb-3" style={{ color: 'var(--olive)' }}>No tags yet — add tags to members in the Members tab first.</p>
              ) : (
                <div className="flex flex-wrap gap-2 mb-3" data-testid="email-tag-picker">
                  {allTags.map((t) => (
                    <button key={t} onClick={() => toggleEmailTag(t)} data-testid={`email-tag-${t}`}
                      className="px-2.5 py-1 rounded text-xs font-title" style={emailTags.includes(t) ? { background: 'var(--maroon)', color: 'var(--cream)' } : { background: 'var(--sage)', color: '#3a2c06' }}>
                      {t}
                    </button>
                  ))}
                </div>
              )}

              {emailTags.length > 1 && (
                <div className="flex items-center gap-3 mb-3 text-sm">
                  <span style={{ color: 'var(--olive)' }}>Match:</span>
                  <label className="flex items-center gap-1"><input type="radio" checked={emailMatch === 'any'} onChange={() => setEmailMatch('any')} data-testid="email-match-any" /> any tag</label>
                  <label className="flex items-center gap-1"><input type="radio" checked={emailMatch === 'all'} onChange={() => setEmailMatch('all')} data-testid="email-match-all" /> all tags</label>
                </div>
              )}

              <label className="flex items-center gap-1.5 text-sm mb-3" data-testid="email-include-inactive">
                <input type="checkbox" checked={emailIncludeInactive} onChange={(e) => setEmailIncludeInactive(e.target.checked)} data-testid="email-include-inactive-checkbox" /> Include deceased, resigned and no-contact members
              </label>

              <label className="text-sm block mb-1" style={{ color: 'var(--navy)' }}>Subject</label>
              <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} placeholder="e.g. Committee meeting — March" data-testid="email-subject" className="mb-3 h-10" />

              <label className="text-sm block mb-1" style={{ color: 'var(--navy)' }}>Message</label>
              <textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} rows={7} placeholder="Dear {{name}}, …" data-testid="email-body" className="w-full px-3 py-2 rounded border text-sm mb-3" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />

              <button onClick={sendEmailGroup} disabled={emailBusy || !emailTags.length || !emailSubject.trim() || !emailBody.trim() || emailRecipients.length === 0}
                data-testid="email-send-btn"
                className="flex items-center gap-2 px-5 py-2.5 rounded font-title text-sm"
                style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: (emailBusy || !emailTags.length || !emailSubject.trim() || !emailBody.trim() || emailRecipients.length === 0) ? 0.6 : 1 }}>
                <Send size={15} /> {emailBusy ? 'Sending…' : `Send to ${emailRecipients.length} member${emailRecipients.length === 1 ? '' : 's'}`}
              </button>

              {emailResult && (
                <p className="text-sm mt-3" data-testid="email-result" style={{ color: emailResult.error ? '#8a1f11' : 'var(--navy)' }}>
                  {emailResult.error ? emailResult.error
                    : emailResult.mocked ? `Logged ${emailResult.sent} message(s) in demo mode (not delivered — connect a provider to send for real).`
                    : `Sent ${emailResult.sent} of ${emailResult.recipients} email(s).`}
                </p>
              )}
            </div>

            <div className="bg-white rounded-lg shadow-sm border p-5" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <h4 className="font-title text-base mb-2" style={{ color: 'var(--navy)' }}>Recipients ({emailRecipients.length})</h4>
              {emailTags.length === 0 ? (
                <p className="text-sm" style={{ color: 'var(--olive)' }}>Select a tag to preview who will receive this email.</p>
              ) : emailRecipients.length === 0 ? (
                <p className="text-sm" style={{ color: 'var(--olive)' }}>No members match the selected tag(s).</p>
              ) : (
                <ul className="space-y-1 max-h-[420px] overflow-y-auto" data-testid="email-recipient-list">
                  {emailRecipients.map((u) => (
                    <li key={u.user_id} className="text-sm flex justify-between border-b py-1" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                      <span>{u.name}</span>
                      <span style={{ color: 'var(--olive)' }}>{u.email}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border p-5" data-testid="email-history" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <h4 className="flex items-center gap-2 font-title text-base mb-3" style={{ color: 'var(--navy)' }}><Clock size={16} /> Send history</h4>
              {emailHistory.length === 0 ? (
                <p className="text-sm" style={{ color: 'var(--olive)' }}>No group emails sent yet.</p>
              ) : (
                <ul className="space-y-2">
                  {emailHistory.map((h) => (
                    <li key={h.id} className="border rounded p-3" style={{ borderColor: 'rgba(0,0,0,0.1)' }} data-testid={`email-history-${h.id}`}>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium">{h.subject}</p>
                          <p className="text-xs" style={{ color: 'var(--olive)' }}>
                            {(h.tags || []).join(', ')} · {h.recipient_count} recipient{h.recipient_count === 1 ? '' : 's'} · {new Date(h.sent_at).toLocaleString()}
                            {h.mocked ? ' · demo (not delivered)' : ''}
                          </p>
                        </div>
                        <button className="text-xs underline" style={{ color: 'var(--navy)' }} data-testid={`email-history-toggle-${h.id}`}
                          onClick={() => setExpandedSend(expandedSend === h.id ? null : h.id)}>
                          {expandedSend === h.id ? 'Hide recipients' : 'Show recipients'}
                        </button>
                      </div>
                      {expandedSend === h.id && (
                        <ul className="mt-2 pl-3 space-y-0.5">
                          {(h.recipients || []).map((r, i) => (
                            <li key={i} className="text-xs flex justify-between" style={{ color: 'var(--olive)' }}>
                              <span>{r.name}</span><span>{r.email}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {tab === 'inbox' && <InboxTab />}

        {tab === 'renewals' && <RenewalsTab />}


        {tab === 'settings' && (
          <div className="max-w-2xl" data-testid="email-settings-panel">
            <div className="flex items-center gap-2 mb-1">
              <Cog size={18} style={{ color: 'var(--maroon)' }} />
              <h2 className="font-title text-xl" style={{ color: 'var(--maroon)' }}>Email Settings</h2>
            </div>
            <p className="text-sm mb-5" style={{ color: 'var(--olive)' }}>
              Configure how the portal sends email (renewal reminders, group emails, receipts, password resets and forwards).
              Leave the provider on <strong>Mock</strong> to keep logging emails without sending.
            </p>

            {emailCfg === null ? (
              <p className="text-sm" style={{ color: 'var(--olive)' }}>Loading…</p>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <span className="text-sm px-2 py-1 rounded" data-testid="email-status-badge"
                    style={{ background: emailCfg.configured ? '#eaf6ec' : '#fdf0e3', color: emailCfg.configured ? '#1c6b2c' : '#8a5a11' }}>
                    {emailCfg.configured ? 'Live sending configured' : 'Mock (emails logged, not sent)'}
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1" style={{ color: 'var(--ink)' }}>Provider</label>
                  <Select value={emailCfg.provider} onValueChange={(v) => { setEmailCfgSaved(false); setEmailCfg((c) => ({ ...c, provider: v })); }}>
                    <SelectTrigger className="w-64" data-testid="email-provider-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mock">Mock (log only)</SelectItem>
                      <SelectItem value="smtp">SMTP server</SelectItem>
                      <SelectItem value="resend">Resend</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold mb-1" style={{ color: 'var(--ink)' }}>From email</label>
                    <Input value={emailCfg.from_email || ''} placeholder="secretary@rae.org.au"
                      onChange={(e) => { setEmailCfgSaved(false); setEmailCfg((c) => ({ ...c, from_email: e.target.value })); }} data-testid="email-from-email" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1" style={{ color: 'var(--ink)' }}>From name</label>
                    <Input value={emailCfg.from_name || ''} placeholder="RAE Association of Tasmania"
                      onChange={(e) => { setEmailCfgSaved(false); setEmailCfg((c) => ({ ...c, from_name: e.target.value })); }} data-testid="email-from-name" />
                  </div>
                </div>

                {emailCfg.provider === 'smtp' && (
                  <div className="space-y-3 p-4 rounded border" style={{ borderColor: 'rgba(0,0,0,0.12)', background: 'rgba(0,0,0,0.02)' }}>
                    <div className="flex flex-wrap items-center gap-2" data-testid="smtp-presets">
                      <span className="text-xs" style={{ color: 'var(--olive)' }}>Quick setup:</span>
                      {[
                        { id: 'gmail', label: 'Gmail', host: 'smtp.gmail.com', port: 587 },
                        { id: 'office365', label: 'Office 365', host: 'smtp.office365.com', port: 587 },
                      ].map((p) => (
                        <button key={p.id} type="button" data-testid={`smtp-preset-${p.id}`}
                          onClick={() => { setEmailCfgSaved(false); setEmailCfg((c) => ({ ...c, smtp_host: p.host, smtp_port: p.port, smtp_use_tls: true })); }}
                          className="px-2.5 py-1 rounded text-xs font-title"
                          style={{ background: 'white', border: '1px solid rgba(0,0,0,0.15)', color: 'var(--navy)' }}>
                          {p.label}
                        </button>
                      ))}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-semibold mb-1" style={{ color: 'var(--ink)' }}>SMTP host</label>
                        <Input value={emailCfg.smtp_host || ''} placeholder="smtp.gmail.com"
                          onChange={(e) => { setEmailCfgSaved(false); setEmailCfg((c) => ({ ...c, smtp_host: e.target.value })); }} data-testid="smtp-host" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-1" style={{ color: 'var(--ink)' }}>Port</label>
                        <Input value={emailCfg.smtp_port || 587} type="number"
                          onChange={(e) => { setEmailCfgSaved(false); setEmailCfg((c) => ({ ...c, smtp_port: e.target.value })); }} data-testid="smtp-port" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-1" style={{ color: 'var(--ink)' }}>Username</label>
                      <Input value={emailCfg.smtp_username || ''} placeholder="user@example.com"
                        onChange={(e) => { setEmailCfgSaved(false); setEmailCfg((c) => ({ ...c, smtp_username: e.target.value })); }} data-testid="smtp-username" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-1" style={{ color: 'var(--ink)' }}>
                        Password {emailCfg.smtp_password_set && <span className="font-normal" style={{ color: 'var(--olive)' }}>(saved — leave blank to keep)</span>}
                      </label>
                      <Input type="password" value={emailCfg.smtp_password || ''} placeholder={emailCfg.smtp_password_set ? '••••••••' : 'SMTP password'}
                        onChange={(e) => { setEmailCfgSaved(false); setEmailCfg((c) => ({ ...c, smtp_password: e.target.value })); }} data-testid="smtp-password" />
                    </div>
                    <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--ink)' }}>
                      <Switch checked={!!emailCfg.smtp_use_tls} onCheckedChange={(v) => { setEmailCfgSaved(false); setEmailCfg((c) => ({ ...c, smtp_use_tls: v })); }} data-testid="smtp-tls" />
                      Use STARTTLS (recommended)
                    </label>
                  </div>
                )}

                {emailCfg.provider === 'resend' && (
                  <div className="space-y-3 p-4 rounded border" style={{ borderColor: 'rgba(0,0,0,0.12)', background: 'rgba(0,0,0,0.02)' }}>
                    <div>
                      <label className="block text-sm font-semibold mb-1" style={{ color: 'var(--ink)' }}>
                        Resend API key {emailCfg.resend_api_key_set && <span className="font-normal" style={{ color: 'var(--olive)' }}>(saved — leave blank to keep)</span>}
                      </label>
                      <Input type="password" value={emailCfg.resend_api_key || ''} placeholder={emailCfg.resend_api_key_set ? '••••••••' : 're_xxxxxxxx'}
                        onChange={(e) => { setEmailCfgSaved(false); setEmailCfg((c) => ({ ...c, resend_api_key: e.target.value })); }} data-testid="resend-api-key" />
                      <p className="text-xs mt-1" style={{ color: 'var(--olive)' }}>Get a key at resend.com/api-keys. The From email domain must be verified in Resend.</p>
                    </div>
                  </div>
                )}

                {emailCfgErr && <p className="text-sm" style={{ color: '#8a1f11' }} data-testid="email-settings-error">{emailCfgErr}</p>}
                <div className="flex items-center gap-3">
                  <button onClick={saveEmailCfg} disabled={emailCfgBusy}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded font-semibold"
                    style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: emailCfgBusy ? 0.7 : 1 }} data-testid="email-settings-save">
                    <Save size={16} /> Save email settings
                  </button>
                  {emailCfgSaved && <span className="inline-flex items-center gap-1 text-sm" style={{ color: '#1c6b2c' }} data-testid="email-settings-saved"><Check size={15} /> Saved</span>}
                </div>

                <div className="mt-5 pt-5 border-t" style={{ borderColor: 'rgba(0,0,0,0.1)' }}>
                  <label className="block text-sm font-semibold mb-1" style={{ color: 'var(--ink)' }}>Send a test email</label>
                  <p className="text-xs mb-2" style={{ color: 'var(--olive)' }}>Save your settings first, then send a test to confirm delivery works.</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <Input value={testTo} placeholder={emailCfg.from_email ? 'Recipient (blank = your own email)' : 'Recipient email'}
                      onChange={(e) => setTestTo(e.target.value)} className="w-72" data-testid="test-email-to" />
                    <button onClick={doSendTestEmail} disabled={testBusy}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded font-semibold"
                      style={{ background: 'var(--navy)', color: 'var(--cream)', opacity: testBusy ? 0.7 : 1 }} data-testid="test-email-send">
                      <Send size={16} /> {testBusy ? 'Sending…' : 'Send test email'}
                    </button>
                  </div>
                  {testMsg && (
                    <p className="text-sm mt-2" data-testid="test-email-result"
                      style={{ color: testMsg.ok ? '#1c6b2c' : '#8a5a11' }}>{testMsg.text}</p>
                  )}
                </div>
                <SmsSettingsCard />
              </div>
            )}
          </div>
        )}


        {tab === 'photos' && <PhotoModerationTab />}
        {tab === 'actions' && <AdminActionsTab />}
        {tab === 'meetings' && <MeetingsAdminTab />}
        {tab === 'faces' && <FacesTab />}
        {tab === 'history' && <HistoryTab />}
        {tab === 'motions' && <MotionsTab />}
        {tab === 'payments' && <PaymentsTab />}
        {tab === 'invoices' && <InvoicesTab />}
        {tab === 'emails' && <EmailsTab />}

        {tab === 'events' && <EventsAdminTab />}

        {tab === 'google' && <GoogleWorkspaceTab />}

        {tab === 'banking' && <BankingTab />}

        {tab === 'organisation' && <OrganisationTab />}

        {tab === 'tags' && <TagsTab />}

        {tab === 'shop' && <ShopAdminTab />}

        {tab === 'subgroups' && <SubgroupsAdminTab />}

        {tab === 'articles' && <ArticlesTab />}

        {tab === 'gallery' && <GalleryEditorTab />}

        {tab === 'governance' && <GovernanceDocsTab />}

        {tab === 'migration' && <MigrationTab />}

        {tab === 'pitch' && <PitchDeckTab />}
        {tab === 'photobooks' && <PhotoBooksAdminTab />}
        {tab === 'raffles' && <RafflesAdminTab />}
        {tab === 'transactions' && <TransactionsTab />}
        {tab === 'enquiries' && <EnquiriesTab />}
        {tab === 'email-queue' && <EmailQueueTab />}
        {tab === 'help' && <HelpTab />}
        {tab === 'ops' && <OpsDashboard />}
        {tab === 'albums' && <AlbumsAdminTab />}
        {tab === 'message' && <MessageTab />}
        {tab === 'memorabilia' && (
          <div className="rounded-lg border bg-white p-5 text-sm" style={{ borderColor: 'rgba(0,0,0,0.12)' }} data-testid="memorabilia-admin">
            <p className="mb-3">The collection register lives on the <Link to="/memorabilia" className="underline font-title" style={{ color: 'var(--maroon)' }} data-testid="memorabilia-admin-open">Memorabilia page</Link>: search, sort, filter and group the master list, double-click a record to edit it, link photos from the <Link to="/members/photos/album/Memorabilia" className="underline">Memorabilia album</Link>, copy image descriptions, print RAE Tas / AMaGA catalogue records and build albums from a selection.</p>
            <p className="text-xs" style={{ color: 'var(--olive)' }}>Who can edit is controlled by the "Memorabilia" row in Page Permissions (admin section). Members with access to the Memorabilia page can view and print.</p>
          </div>
        )}

        {tab === 'applications' && <ApplicationsTab />}


        {tab === 'activity' && <AnalyticsTab />}


        {tab === 'appearance' && (
          <div>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <p className="text-sm" style={{ color: 'var(--olive)' }}>
                Customise the site theme, fonts, header text and layout. Changes apply to all visitors after saving.
              </p>
              <div className="flex gap-2">
                <button onClick={resetAppearance} className="flex items-center gap-1.5 px-3 py-2 rounded text-sm font-title" style={{ background: 'white', border: '1px solid rgba(0,0,0,0.15)' }}>
                  <RotateCcw size={14} /> Reset defaults
                </button>
                <button onClick={saveAppearance} className="flex items-center gap-1.5 px-4 py-2 rounded text-sm font-title transition-colors" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>
                  <Save size={14} /> Save appearance
                </button>
              </div>
            </div>
            {appSaved && <p className="text-sm mb-4" style={{ color: 'var(--navy)' }}>Appearance saved and applied.</p>}

            <div className="grid md:grid-cols-2 gap-6">
              {/* Colours */}
              <div className="bg-white rounded-lg p-5 shadow-sm border" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
                <h3 className="font-title text-lg mb-3" style={{ color: 'var(--navy)' }}>Theme Colours</h3>
                <div className="space-y-3">
                  {COLOR_FIELDS.map((f) => (
                    <div key={f.key} className="flex items-center justify-between">
                      <span className="text-sm">{f.label}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono" style={{ color: 'var(--olive)' }}>{(() => { const v = appDraft.colors[f.key] || ''; return v.startsWith('#') ? v.toUpperCase() : v; })()}</span>
                        <AlphaColorPicker
                          value={appDraft.colors[f.key] || '#000000'}
                          onChange={(val) => setColor(f.key, val)}
                          testId={`appearance-color-${f.key}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Fonts + header text */}
              <div className="space-y-6">
                <div className="bg-white rounded-lg p-5 shadow-sm border" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
                  <h3 className="font-title text-lg mb-3" style={{ color: 'var(--navy)' }}>Fonts</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm block mb-1">Heading font</label>
                      <Select value={appDraft.fonts.title} onValueChange={(v) => setFont('title', v)}>
                        <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {FONT_OPTIONS.map((f) => (<SelectItem key={f} value={f}>{f}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm block mb-1">Body font</label>
                      <Select value={appDraft.fonts.body} onValueChange={(v) => setFont('body', v)}>
                        <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {FONT_OPTIONS.map((f) => (<SelectItem key={f} value={f}>{f}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-5 shadow-sm border" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
                  <h3 className="font-title text-lg mb-3" style={{ color: 'var(--navy)' }}>Header Text</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm block mb-1">Association title</label>
                      <Input value={appDraft.header.title} onChange={(e) => setHeader('title', e.target.value)} />
                    </div>
                    <div>
                      <label className="text-sm block mb-1">Motto / tagline</label>
                      <Input value={appDraft.header.motto} onChange={(e) => setHeader('motto', e.target.value)} />
                    </div>
                    <div>
                      <label className="text-sm block mb-1">Prefix word (e.g. Ubique)</label>
                      <Input value={appDraft.header.ubique} onChange={(e) => setHeader('ubique', e.target.value)} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Layout */}
            <div className="bg-white rounded-lg p-5 shadow-sm border mt-6" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <h3 className="font-title text-lg mb-3" style={{ color: 'var(--navy)' }}>Layout</h3>
              <div className="grid sm:grid-cols-3 gap-4 mb-5">
                {[
                  { key: 'showHomeCards', label: 'Home quick-link cards' },
                  { key: 'showUbique', label: 'Motto line in header' },
                  { key: 'showSocial', label: 'Footer social icons' },
                ].map((f) => (
                  <label key={f.key} className="flex items-center justify-between gap-3 text-sm">
                    <span>{f.label}</span>
                    <Switch checked={appDraft.layout[f.key] !== false} onCheckedChange={(v) => setLayoutFlag(f.key, v)} />
                  </label>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mb-5 max-w-md">
                <label className="text-sm">
                  <span className="block mb-1" style={{ color: 'var(--navy)' }}>Header crest width (px)</span>
                  <input type="number" min={40} max={260} value={appDraft.layout.crestWidth || 120}
                    onChange={(e) => setLayoutFlag('crestWidth', Number(e.target.value))} data-testid="crest-width"
                    className="w-full px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
                </label>
                <label className="text-sm">
                  <span className="block mb-1" style={{ color: 'var(--navy)' }}>Header crest height (px)</span>
                  <input type="number" min={40} max={200} value={appDraft.layout.crestHeight || 90}
                    onChange={(e) => setLayoutFlag('crestHeight', Number(e.target.value))} data-testid="crest-height"
                    className="w-full px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
                </label>
              </div>
              <div className="flex flex-wrap gap-2">
                {NAV_ITEMS.map((item) => {
                  const hidden = (appDraft.layout.hiddenNav || []).includes(item.label);
                  return (
                    <button
                      key={item.label}
                      onClick={() => toggleHiddenNav(item.label)}
                      className="px-3 py-1.5 rounded text-sm font-title transition-colors"
                      style={hidden
                        ? { background: 'white', color: 'var(--olive)', border: '1px solid rgba(0,0,0,0.15)', textDecoration: 'line-through' }
                        : { background: 'var(--navy)', color: 'var(--cream)' }}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs mt-2" style={{ color: 'var(--olive)' }}>Blue = visible, struck-through = hidden. (Home is recommended to keep visible.)</p>
            </div>

            {/* Templates */}
            <div className="bg-white rounded-lg p-5 shadow-sm border mt-6" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <div className="flex items-center gap-2 mb-1">
                <LayoutTemplate size={18} style={{ color: 'var(--navy)' }} />
                <h3 className="font-title text-lg" style={{ color: 'var(--navy)' }}>Saved Templates</h3>
              </div>
              <p className="text-sm mb-3" style={{ color: 'var(--olive)' }}>
                Save the current styles, header and footer as a reusable template. Apply a template any time to restore that look across the whole site.
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                <Input
                  data-testid="template-name-input"
                  value={tplName}
                  onChange={(e) => setTplName(e.target.value)}
                  placeholder="Template name, e.g. Anzac Day 2026"
                  className="flex-1 min-w-[200px] h-10"
                />
                <button
                  data-testid="template-save-btn"
                  onClick={saveCurrentTemplate}
                  disabled={tplBusy || !tplName.trim()}
                  className="flex items-center gap-2 px-4 py-2 rounded font-title text-sm"
                  style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: tplBusy || !tplName.trim() ? 0.6 : 1 }}
                >
                  <Save size={15} /> Save current as template
                </button>
              </div>
              {tplMsg && <p className="text-sm mb-3" style={{ color: 'var(--navy)' }} data-testid="template-msg">{tplMsg}</p>}

              {templates.length === 0 ? (
                <p className="text-sm" style={{ color: 'var(--olive)' }}>No templates saved yet.</p>
              ) : (
                <ul className="space-y-2" data-testid="template-list">
                  {templates.map((t) => (
                    <li key={t.template_id} className="flex items-center justify-between rounded p-3 border" style={{ borderColor: 'rgba(0,0,0,0.1)' }}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="flex gap-1">
                          {['maroon', 'navy', 'gold', 'sage'].map((c) => (
                            <span key={c} className="w-4 h-4 rounded-full border" style={{ background: (t.settings && t.settings.colors && t.settings.colors[c]) || '#ccc', borderColor: 'rgba(0,0,0,0.15)' }} />
                          ))}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{t.name}</p>
                          <p className="text-xs truncate" style={{ color: 'var(--olive)' }}>
                            {(t.settings && t.settings.fonts && t.settings.fonts.title) || '—'} · {(t.settings && t.settings.header && t.settings.header.title) || ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                          data-testid={`template-apply-${t.template_id}`}
                          onClick={() => applyTpl(t)}
                          disabled={tplBusy}
                          className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-title"
                          style={{ background: 'var(--navy)', color: 'var(--cream)' }}
                        >
                          <Check size={13} /> Apply
                        </button>
                        <button
                          data-testid={`template-delete-${t.template_id}`}
                          onClick={() => removeTpl(t)}
                          disabled={tplBusy}
                          title="Delete"
                          className="p-1.5 rounded"
                          style={{ background: 'var(--maroon)', color: 'var(--cream)' }}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </AccessGate>
  );
}
